/**
 * Bolão de Futebol - Aplicação Completa
 *
 * Esta aplicação permite que usuários se cadastrem, criem bolões de campeonatos de futebol,
 * convidem amigos e façam suas apostas. O sistema calcula a pontuação e exibe o ranking
 * em tempo real.
 *
 * Funcionalidades:
 * 1. Autenticação de Usuários (Cadastro, Login e Recuperação de Senha).
 * 2. Criação de Bolões baseados em campeonatos pré-definidos (Liga ou Copa), com campos para regras e premiação.
 * 3. Gerenciamento de fases, grupos e times (com upload de escudos) para campeonatos.
 * 4. Geração de link de convite e gerenciamento de participantes do bolão (inativar/reativar).
 * 5. Interface para fazer apostas nos jogos, com trava de tempo e opção de "Partida Bônus".
 * 6. Cálculo automático de pontuação e exibição de ranking com critérios de desempate.
 * 7. Painel de Administração para gerenciar campeonatos, fases, e resultados (incluindo prorrogação/pênaltis).
 * - Corrigi o useEffect de PoolView para depender apenas de poolId (evita re-subscrições infinitas).
 *
 * Tecnologia:
 * - Frontend: React com Hooks
 * - Estilização: Tailwind CSS
 * - Backend e Banco de Dados: Firebase (Authentication, Firestore, Storage)
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithCredential
} from 'firebase/auth';
import {
  doc,
  setDoc,
  getDoc,
  addDoc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  arrayUnion,
  onSnapshot,
  arrayRemove,
  deleteDoc,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { PushNotifications } from '@capacitor/push-notifications';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Share } from '@capacitor/share';
import GoogleIcon from './assets/google-icon.png';
import { auth, db, storage, googleProvider } from './firebaseConfig';
import F1TeamsManager from './f1/F1TeamsManager';
import F1DriversManager from './f1/F1DriversManager';
import F1StandingsAdmin from './f1/F1StandingsAdmin';
import { runDailyReminder } from './backend/dailyReminder';
import LandingPage from './LandingPage';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfService from './TermsOfService'; // Import new component
import ExclusaoDados from './ExclusaoDados';
import F1CustomSelect from './f1/F1CustomSelect';
import BolaoCopa2026 from './BolaoCopa2026';
import RegrasBolaoOnline from './RegrasBolaoOnline';
import OrganizarBolaoAmigos from './OrganizarBolaoAmigos';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import AdminDashboard from './AdminDashboard';

// --- Sincronização de Tempo ---
let syncServerTime = 0;
let syncPerfTime = 0;
let isTimeSynced = false;

const applyTimeSync = (serverTimeMs, sourceName) => {
  if (!serverTimeMs || isNaN(serverTimeMs)) {
    throw new Error("Invalid Server Time");
  }
  syncServerTime = serverTimeMs;
  syncPerfTime = performance.now();
  isTimeSynced = true;
  console.log(`Relógio sincronizado via ${sourceName}. Diferença p/ local:`, serverTimeMs - Date.now());
};

export const syncTime = async (force = false) => {
  if (isTimeSynced && !force) return;
  isTimeSynced = false;
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.startsWith('192.168.');
  
  if (!isLocalhost) {
    try {
      const res = await fetch(window.location.origin, { method: 'HEAD', cache: 'no-store' });
      const dateStr = res.headers.get('date');
      if (dateStr) {
        applyTimeSync(new Date(dateStr).getTime(), "servidor origin");
        return;
      }
    } catch (error) {
      console.warn("Falha ao sincronizar relógio via origin...", error);
    }
  }

  // Tenta timeapi.io primeiro
  try {
    const res = await fetch('https://timeapi.io/api/Time/current/zone?timeZone=UTC', { cache: 'no-store' });
    const data = await res.json();
    applyTimeSync(new Date(data.dateTime + 'Z').getTime(), "TimeAPI");
    return;
  } catch (e) {
    console.warn("Falha ao sincronizar relógio via TimeAPI. Tentando fallback...");
  }

  // Fallback para worldtimeapi
  try {
    const res = await fetch('https://worldtimeapi.org/api/timezone/Etc/UTC', { cache: 'no-store' });
    const data = await res.json();
    applyTimeSync(new Date(data.utc_datetime).getTime(), "WorldTimeAPI");
  } catch (e) {
    console.error("Falha total ao sincronizar relógio. Usando hora local.");
    syncServerTime = Date.now();
    syncPerfTime = performance.now();
    // Deixa isTimeSynced = false para tentar novamente depois
  }
};

export const getRealDate = () => {
  if (syncServerTime === 0) {
    return new Date(); // Fallback antes do sync
  }
  const elapsed = performance.now() - syncPerfTime;
  const current = syncServerTime + elapsed;
  if (isNaN(current)) return new Date(); // Prevenção contra Invalid Date
  return new Date(current);
};

// --- Constantes e Helpers ------------------------------------------------
const ADMIN_UID = "GCnCYeeHXWOaEcqtehdJOwzdhyC3";

const getPointsInfo = (bet, result, pool = null) => {
  if (!bet || result.resultA === null || result.resultB === null) {
    return { points: 0, type: null, rawPoints: 0 };
  }
  const betA = parseInt(bet.betA, 10);
  const betB = parseInt(bet.betB, 10);
  if (isNaN(betA) || isNaN(betB)) {
    return { points: 0, type: null, rawPoints: 0 };
  }
  const resultA = result.resultA;
  const resultB = result.resultB;
  let rawPoints = 0;
  let type = null;
  if (betA === resultA && betB === resultB) {
    rawPoints = 10;
    type = 'exact';
  } else {
    const betWinner = betA > betB ? 'A' : betA < betB ? 'B' : 'D';
    const resultWinner = resultA > resultB ? 'A' : resultA < resultB ? 'B' : 'D';
    if (betWinner === resultWinner && betWinner !== 'D') {
      if ((betWinner === 'A' && betA === resultA) || (betWinner === 'B' && betB === resultB)) {
        rawPoints = 6;
        type = 'winner_goals';
      } else {
        rawPoints = 3;
        type = 'winner';
      }
    } else if (resultWinner === 'D' && betWinner === 'D') {
      rawPoints = 5;
      type = 'draw';
    }
  }
  let finalPoints = bet.isBonus ? rawPoints * 2 : rawPoints;
  
  if (pool && pool.knockoutMultiplierPhase && pool.knockoutMultiplierPhase !== 'Nenhuma') {
    const matchPhaseValue = getPhaseSortValue(result.phase);
    const multiplierPhaseValue = getPhaseSortValue(pool.knockoutMultiplierPhase);
    
    // Apenas dobra os pontos se for uma fase de mata-mata conhecida (entre 50 e 400)
    // Fases customizadas não mapeadas retornam 500 e não devem ter a pontuação dobrada automaticamente
    if (matchPhaseValue >= 50 && matchPhaseValue <= 400 && matchPhaseValue >= multiplierPhaseValue) {
      finalPoints *= 2;
    }
  }

  return { points: finalPoints, type: type, rawPoints: rawPoints };
};

const getF1PointsInfo = (bet, match) => {
  if (!bet || !match.resultP1 || !match.resultP2 || !match.resultP3) {
    return { points: 0, type: null, rawPoints: 0 };
  }

  let points = 0;
  let hits = 0;

  if (bet.p1 === match.resultP1) { points += 10; hits++; }
  if (bet.p2 === match.resultP2) { points += 6; hits++; }
  if (bet.p3 === match.resultP3) { points += 3; hits++; }

  // Bônus de pódio completo (acertou os 3 na posição exata)
  if (hits === 3) {
    points += 1;
  }

  return { points, type: 'f1_race', rawPoints: points };
};

const getF1ScoreDetails = (bet, match) => {
  if (!bet || !match.resultP1 || !match.resultP2 || !match.resultP3) return null;

  let points = 0;
  let hits = [];
  let exactHits = 0;

  if (bet.p1 === match.resultP1) { points += 10; hits.push("1º Lugar"); exactHits++; }
  if (bet.p2 === match.resultP2) { points += 6; hits.push("2º Lugar"); exactHits++; }
  if (bet.p3 === match.resultP3) { points += 3; hits.push("3º Lugar"); exactHits++; }

  let breakdown = "";
  if (exactHits === 3) {
    points += 1;
    breakdown = "Acertou tudo ! 19 Pontos + 1 ponto extra";
  } else if (hits.length > 0) {
    const last = hits.pop();
    const text = hits.length > 0 ? hits.join(", ") + " e o " + last : last;
    breakdown = `Acertou o ${text}`;
  } else {
    breakdown = "Não pontuou";
  }

  return { points, breakdown };
};

/**
 * Componente de Botão de Aba
 */
/**
 * Helper para ordenar as fases do campeonato na ordem correta.
 */
const getPhaseSortValue = (phase) => {
  if (!phase) return 500;
  
  const cleanPhase = phase.trim().toLowerCase();
  
  if (cleanPhase.startsWith('rodada ')) {
    // Extrai o número da rodada e retorna
    return parseInt(cleanPhase.split(' ')[1], 10) || 0;
  }
  // Define pesos altos para as fases de mata-mata
  switch (cleanPhase) {
    case '16 avos':
    case '16-avos':
      return 50;
    case 'oitavas de final':
    case 'oitavas':
      return 100; // Assumindo no máximo 99 rodadas
    case 'quartas de final':
    case 'quartas':
      return 200;
    case 'semi final': // Nome usado pelo usuário
    case 'semifinal': // Grafia comum
      return 300;
    case 'disputa de 3º lugar':
    case 'disputa 3º lugar':
    case '3º lugar':
    case 'terceiro lugar':
      return 350;
    case 'final':
      return 400;
    case 'jogos gerais':
      return -1; // Coloca "Jogos Gerais" no início
    default:
      return 500; // Coloca fases desconhecidas no final
  }
};

const Icon = ({ name }) => {
  const icons = {
    users: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
        d="M17 20h5v-1a4 4 0 00-4-4h-1M9 20H4v-1a4 4 0 014-4h1m0 0a4 4 0 100-8 4 4 0 000 8z" />
    ),
    plus: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
        d="M12 4v8m0 0v8m0-8h8m-8 0H4" />
    ),
    trophy: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
        d="M8 7V5a4 4 0 018 0v2a4 4 0 01-8 0zM5 9h14v2a6 6 0 01-6 6h0a6 6 0 01-6-6V9zM9 21h6" />
    ),
    logout: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
    ),
    copy: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
        d="M8 7h8a2 2 0 012 2v8a2 2 0 01-2 2H8a2 2 0 01-2-2V9a2 2 0 012-2zM8 3h8a2 2 0 012 2" />
    ),
    check: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
        d="M5 13l4 4L19 7" />
    ),
    arrowLeft: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
        d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    ),
    share: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
        d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
    ),
    spinner: (
      <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm0 0v4" />
    ),
  };
  const spinClass = name === 'spinner' ? 'animate-spin' : '';
  return (
    <svg className={`w-6 h-6 inline-block mr-2 ${spinClass}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      {icons[name] || null}
    </svg>
  );
};

/**
 * Componente de Autocomplete para campos de texto
 */
const AutocompleteInput = ({ value, onChange, placeholder, suggestions }) => {
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const handleChange = (e) => {
    const userInput = e.currentTarget.value;
    let newFilteredSuggestions = [];
    if (userInput) {
      newFilteredSuggestions = suggestions.filter(
        suggestion =>
          suggestion.toLowerCase().indexOf(userInput.toLowerCase()) > -1
      );
    }

    onChange(userInput);
    setFilteredSuggestions(newFilteredSuggestions);
    setShowSuggestions(true);
  };

  const handleSuggestionClick = (suggestion) => {
    onChange(suggestion);
    setFilteredSuggestions([]);
    setShowSuggestions(false);
  };

  return (
    <div className="relative">
      <input
        type="text"
        onChange={handleChange}
        onFocus={() => { if (value) setShowSuggestions(true); }}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 150)} // Delay para permitir o clique na sugestão
        value={value}
        placeholder={placeholder}
        className="bg-gray-700 p-2 rounded-md w-full"
        autoComplete="off"
      />
      {showSuggestions && value && filteredSuggestions.length > 0 && (
        <ul className="absolute z-20 w-full bg-gray-600 border border-gray-500 rounded-md mt-1 max-h-40 overflow-y-auto shadow-lg">
          {filteredSuggestions.map((suggestion, index) => (
            <li
              key={suggestion + index}
              onMouseDown={() => handleSuggestionClick(suggestion)}
              className="p-2 hover:bg-teal-500 cursor-pointer"
            >
              {suggestion}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * Componente para telas de Autenticação (Login e Cadastro)
 */
const AuthComponent = () => {
  const { t } = useTranslation();
  const [authView, setAuthView] = useState('login'); // 'login', 'register', 'reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');
    try {
      if (authView === 'login') {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        await setDoc(doc(db, "users", userCredential.user.uid), { lastLogin: new Date().toISOString() }, { merge: true });
      } else {
        if (!displayName) throw new Error(t('displayNameRequired'));
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        await updateProfile(userCredential.user, { displayName });
        await setDoc(doc(db, "users", userCredential.user.uid), {
          displayName,
          email,
          lastLogin: new Date().toISOString()
        });
      }
    } catch (err) {
      let friendlyMessage = t('defaultError');
      if (err && err.code) {
        switch (err.code) {
          case 'auth/wrong-password':
          case 'auth/invalid-credential':
            friendlyMessage = t('wrongPassword');
            break;
          case 'auth/user-not-found':
            friendlyMessage = t('userNotFound');
            break;
          case 'auth/invalid-email':
            friendlyMessage = t('invalidEmail');
            break;
          case 'auth/email-already-in-use':
            friendlyMessage = t('emailInUse');
            break;
          case 'auth/weak-password':
            friendlyMessage = t('weakPassword');
            break;
          default:
            console.error("Firebase Auth Error:", err);
            break;
        }
        setError(friendlyMessage);
      } else {
        console.error("Firebase Auth Error:", err);
        setError(t('authError') + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = async (provider) => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      let user;
      
      if (Capacitor.isNativePlatform()) {
        const result = await FirebaseAuthentication.signInWithGoogle();
        const credential = GoogleAuthProvider.credential(result.credential?.idToken);
        const authResult = await signInWithCredential(auth, credential);
        user = authResult.user;
      } else {
        const result = await signInWithPopup(auth, provider);
        user = result.user;
      }

      // Verifica se o usuário já existe no Firestore, se não, cria
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (!userDoc.exists()) {
        await setDoc(doc(db, "users", user.uid), {
          displayName: user.displayName || (user.email ? user.email.split('@')[0] : 'Sem nome'),
          email: user.email,
          photoURL: user.photoURL,
          lastLogin: new Date().toISOString()
        });
      } else {
        await setDoc(doc(db, "users", user.uid), { lastLogin: new Date().toISOString() }, { merge: true });
      }
    } catch (err) {
      console.error("Erro no login social:", err);
      // Tratamento específico para popup fechado pelo usuário, se desejar
      if (err.code === 'auth/popup-closed-by-user' || err.message?.includes('canceled')) {
        setError("O login foi cancelado.");
      } else {
        setError("Erro ao entrar com rede social: " + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');
    try {
      await sendPasswordResetEmail(auth, email);
      setMessage("Link para redefinição de senha enviado! Verifique seu e-mail.");
    } catch (err) {
      let friendlyMessage = "Ocorreu um erro ao enviar o e-mail.";
      if (err && (err.code === 'auth/user-not-found' || err.code === 'auth/invalid-email')) {
        friendlyMessage = "Não foi possível encontrar um usuário com este e-mail.";
      }
      setError(friendlyMessage);
    } finally {
      setLoading(false);
    }
  };

  const resetFormState = () => {
    setError('');
    setMessage('');
    setEmail('');
    setPassword('');
    setDisplayName('');
  };

  if (authView === 'reset') {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-gray-800 rounded-2xl shadow-lg p-8">
          <h2 className="text-3xl font-bold text-center text-teal-400 mb-2">Redefinir Senha</h2>
          <p className="text-center text-gray-400 mb-8">Digite seu e-mail para receber o link de redefinição.</p>

          {error && <p className="bg-red-500/20 text-red-300 p-3 rounded-lg mb-4">{error}</p>}
          {message && <p className="bg-green-500/20 text-green-300 p-3 rounded-lg mb-4">{message}</p>}

          <form onSubmit={handlePasswordReset}>
            <div className="mb-4">
              <label className="block text-gray-400 mb-2" htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="seu@email.com"
              />
            </div>
            <button type="submit" disabled={loading} className="w-full bg-teal-500 hover:bg-teal-600 text-white font-bold py-3 px-4 rounded-lg transition duration-300 disabled:bg-gray-500 flex items-center justify-center">
              {loading && <Icon name="spinner" />}
              Enviar Link
            </button>
          </form>
          <p className="text-center mt-6 text-gray-400">
            Lembrou a senha?
            <button onClick={() => { setAuthView('login'); resetFormState(); }} className="text-teal-400 hover:underline font-semibold ml-2">
              Faça Login
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-gray-800 rounded-2xl shadow-lg p-8 relative">
        <div className="absolute top-4 right-4">
          <LanguageSwitcher />
        </div>
        <h2 className="text-3xl font-bold text-center text-teal-400 mb-2 mt-4">{authView === 'login' ? t('login') : t('register')}</h2>
        <p className="text-center text-gray-400 mb-8">{t('welcome')}</p>

        {error && <p className="bg-red-500/20 text-red-300 p-3 rounded-lg mb-4">{error}</p>}

        <form onSubmit={handleAuth}>
          {authView === 'register' && (
            <div className="mb-4">
              <label className="block text-gray-400 mb-2" htmlFor="displayName">{t('displayName')}</label>
              <input
                id="displayName"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder={t('displayName')}
              />
            </div>
          )}
          <div className="mb-4">
            <label className="block text-gray-400 mb-2" htmlFor="email">{t('email')}</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="seu@email.com"
            />
          </div>
          <div className="mb-6">
            <label className="block text-gray-400 mb-2" htmlFor="password">{t('password')}</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="********"
            />
            {authView === 'login' && (
              <div className="text-right mt-2">
                <button
                  type="button"
                  onClick={() => { setAuthView('reset'); resetFormState(); }}
                  className="text-sm text-teal-400 hover:underline font-semibold"
                >
                  {t('forgotPassword')}
                </button>
              </div>
            )}
          </div>
          <button type="submit" disabled={loading} className="w-full bg-teal-500 hover:bg-teal-600 p-2 rounded-md font-bold text-white transition-colors">
            {loading ? <Icon name="spinner" /> : (authView === 'login' ? t('enter') : t('register'))}
          </button>
        </form>

        <div className="mt-4 flex flex-col gap-2">
          <button
            onClick={() => handleSocialLogin(googleProvider)}
            disabled={loading}
            className="w-full bg-white text-gray-800 hover:bg-gray-100 p-2 rounded-md font-bold flex items-center justify-center gap-2 transition-colors border border-gray-300"
          >
            <img src={GoogleIcon} alt="G" className="w-6 h-6" /> {t('signInWithGoogle')}
          </button>
        </div>

        <div className="mt-4 text-center text-sm text-gray-400">
          {authView === 'login' ? t('noAccount') : t('hasAccount')}
          <button onClick={() => { setAuthView(authView === 'login' ? 'register' : 'login'); resetFormState(); }} className="text-teal-400 hover:underline font-semibold ml-2">
            {authView === 'login' ? t('signUp') : t('doLogin')}
          </button>
        </div>
      </div>
    </div>
  );
};

const Dashboard = ({ user, setView, setPoolId }) => {
  const { t } = useTranslation();
  const [myPools, setMyPools] = useState([]);
  const [championships, setChampionships] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newPoolName, setNewPoolName] = useState('');
  const [selectedChamp, setSelectedChamp] = useState('');
  const [newPoolRules, setNewPoolRules] = useState('');
  const [newPoolKnockoutMultiplier, setNewPoolKnockoutMultiplier] = useState('Nenhuma');
  const [newPoolPrizes, setNewPoolPrizes] = useState('');
  const [loading, setLoading] = useState(true);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [joinPoolIdInput, setJoinPoolIdInput] = useState('');
  const [joinMessage, setJoinMessage] = useState({ type: '', text: '' });
  const [showUnsubscribeConfirm, setShowUnsubscribeConfirm] = useState(false);
  const [poolToUnsubscribe, setPoolToUnsubscribe] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [poolToDelete, setPoolToDelete] = useState(null);
  const [dashboardTab, setDashboardTab] = useState('ativos'); // 'ativos', 'encerrados'
  const [newPoolIsPublic, setNewPoolIsPublic] = useState(false);
  const [newPoolImage, setNewPoolImage] = useState(null);

  const fixExistingPools = async () => {
    if (user.uid !== ADMIN_UID) return;
    try {
      setLoading(true);
      const poolsSnapshot = await getDocs(collection(db, "pools"));
      let updatedCount = 0;
      for (const poolDoc of poolsSnapshot.docs) {
        const pool = poolDoc.data();
        let needsUpdate = false;
        const updatedParticipants = [];

        for (const p of pool.participants || []) {
          if (!p.displayName || p.displayName === 'null' || p.displayName === 'Sem nome') {
            const userSnap = await getDoc(doc(db, "users", p.uid));
            if (userSnap.exists()) {
              const userData = userSnap.data();
              if (userData.email) {
                const newName = userData.email.split('@')[0];
                updatedParticipants.push({ ...p, displayName: newName });
                needsUpdate = true;
                continue;
              }
            }
          }
          updatedParticipants.push(p);
        }

        if (needsUpdate) {
          await updateDoc(doc(db, "pools", poolDoc.id), { participants: updatedParticipants });
          updatedCount++;
        }
      }
      alert(`Correção concluída! ${updatedCount} bolões foram atualizados.`);
    } catch (error) {
      console.error("Erro ao corrigir bolões:", error);
      alert("Erro ao corrigir bolões: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchChamps = async () => {
      const q = query(collection(db, "championships"));
      const querySnapshot = await getDocs(q);
      const champs = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setChampionships(champs);
      const activeChamps = champs.filter(c => c.status === 'active');
      if (activeChamps.length > 0) {
        setSelectedChamp(activeChamps[0].id);
      } else if (champs.length > 0) {
        setSelectedChamp(champs[0].id);
      }
    };

    const fetchPools = () => {
      const q = query(collection(db, "pools"), where("participantIds", "array-contains", user.uid));
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const pools = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setMyPools(pools);
        setLoading(false);
      }, (error) => {
        console.error("Erro ao buscar bolões: ", error);
        setLoading(false);
      });
      return unsubscribe;
    };

    fetchChamps();
    const unsubscribePools = fetchPools();

    return () => unsubscribePools();
  }, [user.uid]);

  const handleCreatePool = async (e) => {
    e.preventDefault();
    if (!newPoolName || !selectedChamp) return;

    let photoUrl = null;
    if (newPoolImage && user.uid === ADMIN_UID) {
      try {
        const storageRef = ref(storage, `pool-covers/${newPoolImage.name}_${Date.now()}`);
        await uploadBytes(storageRef, newPoolImage);
        photoUrl = await getDownloadURL(storageRef);
      } catch (error) {
        console.error(t('errorUploadImage'), error);
        alert(t('alertUploadImageError'));
      }
    }

    const newPool = {
      name: newPoolName,
      championshipId: selectedChamp,
      adminId: user.uid,
      participants: [{ uid: user.uid, displayName: user.displayName || (user.email ? user.email.split('@')[0] : 'Sem nome'), status: 'active', paid: false }],
      participantIds: [user.uid],
      rules: newPoolRules,
      prizes: newPoolPrizes,
      knockoutMultiplierPhase: newPoolKnockoutMultiplier,
      isPublic: user.uid === ADMIN_UID ? newPoolIsPublic : false,
      photoUrl: photoUrl
    };

    try {
      const docRef = await addDoc(collection(db, "pools"), newPool);
      setPoolId(docRef.id);
      setView('pool');
      // Reset form
      setNewPoolName('');
      setNewPoolRules('');
      setNewPoolPrizes('');
      setNewPoolKnockoutMultiplier('Nenhuma');
      setNewPoolIsPublic(false);
      setNewPoolImage(null);
      setShowCreate(false);
    } catch (error) {
      console.error(t('errorCreatingPool'), error);
    }
  };

  const handleOpenJoinModal = () => {
    setShowJoinModal(true);
    setJoinPoolIdInput('');
    setJoinMessage({ type: '', text: '' });
  };

  const handleSubmitJoinPool = async (e) => {
    e.preventDefault();
    setJoinMessage({ type: '', text: '' });
    if (!joinPoolIdInput) {
      setJoinMessage({ type: 'error', text: t('pleaseEnterPoolId') });
      return;
    }

    try {
      const poolRef = doc(db, "pools", joinPoolIdInput);
      const poolSnap = await getDoc(poolRef);

      if (poolSnap.exists()) {
        if (poolSnap.data().participantIds && poolSnap.data().participantIds.includes(user.uid)) {
          // Se já participa, apenas navega para o bolão
          setPoolId(joinPoolIdInput);
          setView('pool');
        } else {
          await updateDoc(poolRef, {
            participants: arrayUnion({ uid: user.uid, displayName: user.displayName || (user.email ? user.email.split('@')[0] : 'Sem nome'), status: 'active', paid: false }),
            participantIds: arrayUnion(user.uid)
          });
          setPoolId(joinPoolIdInput);
          setView('pool');
        }
      } else {
        setJoinMessage({ type: 'error', text: t('poolNotFound') });
      }
    } catch (error) {
      console.error(t('errorJoiningPool'), error);
      setJoinMessage({ type: 'error', text: t('joinErrorGeneric') });
    }
  };

  const handleUnsubscribeClick = (poolId) => {
    setPoolToUnsubscribe(poolId);
    setShowUnsubscribeConfirm(true);
  };

  const confirmUnsubscribe = async () => {
    if (!poolToUnsubscribe) return;

    try {
      const poolRef = doc(db, "pools", poolToUnsubscribe);
      const poolSnap = await getDoc(poolRef);
      if (poolSnap.exists()) {
        const poolData = poolSnap.data();
        const updatedParticipants = (poolData.participants || []).filter(p => p.uid !== user.uid);

        await updateDoc(poolRef, {
          participants: updatedParticipants,
          participantIds: arrayRemove(user.uid)
        });
      } else {
        console.error(t('unsubscribeErrorPoolNotFound'));
      }
    } catch (error) {
      console.error(t('unsubscribeError'), error);
    } finally {
      setShowUnsubscribeConfirm(false);
      setPoolToUnsubscribe(null);
    }
  };

  const handleDeleteClick = (poolId) => {
    setPoolToDelete(poolId);
    setShowDeleteConfirm(true);
  };

  const confirmDeletePool = async () => {
    if (!poolToDelete) return;
    try {
      const poolRef = doc(db, "pools", poolToDelete);
      await deleteDoc(poolRef);
    } catch (error) {
      console.error("Erro ao excluir bolão:", error);
    } finally {
      setShowDeleteConfirm(false);
      setPoolToDelete(null);
    }
  };

  const champStatusMap = useMemo(() => new Map(championships.map(c => [c.id, c.status])), [championships]);
  const activePools = useMemo(() => myPools.filter(pool => champStatusMap.get(pool.championshipId) !== 'finished'), [myPools, champStatusMap]);
  const finishedPools = useMemo(() => myPools.filter(pool => champStatusMap.get(pool.championshipId) === 'finished'), [myPools, champStatusMap]);

  const renderPools = (pools) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {pools.map(pool => (
        <div key={pool.id} className="bg-gray-800 rounded-xl p-6 hover:bg-gray-700/80 transition-colors flex flex-col justify-between">
          <div onClick={() => { setPoolId(pool.id); setView('pool'); }} className="cursor-pointer">
            <h3 className="text-xl font-bold text-teal-400">{pool.name}</h3>
            <p className="text-gray-400">
              {t(championships.find(c => c.id === pool.championshipId)?.name) || '—'}
            </p>
            <div className="flex items-center mt-4">
              <Icon name="users" />
              <span className="text-gray-300">{t('participantsCount', { count: (pool.participants || []).length })}</span>
            </div>
          </div>
          {pool.adminId !== user.uid && (
            <div className="mt-4 pt-4 border-t border-gray-700 flex justify-end"> {/* <-- Classe adicionada aqui */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleUnsubscribeClick(pool.id);
                }}
                className="bg-red-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-red-700 transition-colors text-sm" /* <-- Classe 'w-full' removida daqui */
              >
                {t('unsubscribe')}
              </button>
            </div>
          )}
          {pool.adminId === user.uid && (
            <div className="mt-4 pt-4 border-t border-gray-700 flex justify-end">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(pool.id);
                }}
                className="bg-red-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-red-700 transition-colors text-sm"
              >
                Excluir Bolão
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="p-4 md:p-8">
      <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-6 gap-4">
        <h2 className="text-3xl font-bold text-white">{t('myPools')}</h2>
        <div className="flex items-center shrink-0 gap-4">
          <button onClick={() => setShowCreate(!showCreate)} className="bg-teal-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-teal-600 transition-colors flex items-center text-sm">
            <Icon name="plus" /> {t('createPool')}
          </button>
          <button onClick={handleOpenJoinModal} className="bg-blue-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors flex items-center text-sm whitespace-nowrap">
            {t('joinWithId')}
          </button>
          {user.uid === ADMIN_UID && (
            <button onClick={fixExistingPools} className="bg-orange-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-orange-600 transition-colors flex items-center text-sm whitespace-nowrap">
              <Icon name="check" /> Corrigir Nomes
            </button>
          )}
        </div>
      </div>

      <div className="border-b border-gray-700 mb-6 w-full overflow-hidden">
        <nav className="flex space-x-4 overflow-x-auto scrollbar-hide pb-1">
          <button onClick={() => setDashboardTab('ativos')} className={`py-2 px-4 font-semibold transition-colors duration-300 whitespace-nowrap shrink-0 ${dashboardTab === 'ativos' ? 'text-teal-400 border-b-2 border-teal-400' : 'text-gray-400 hover:text-white'}`}>
            {t('activePools')}
          </button>
          <button onClick={() => setDashboardTab('encerrados')} className={`py-2 px-4 font-semibold transition-colors duration-300 whitespace-nowrap shrink-0 ${dashboardTab === 'encerrados' ? 'text-teal-400 border-b-2 border-teal-400' : 'text-gray-400 hover:text-white'}`}>
            {t('finishedPools')}
          </button>
        </nav>
      </div>

      {loading ? <p className="text-gray-400">{t('loading')}</p> : (
        <>
          {dashboardTab === 'ativos' && (
            activePools.length > 0 ? renderPools(activePools) : <p className="text-gray-400 mt-4">{t('noActivePools')}</p>
          )}
          {dashboardTab === 'encerrados' && (
            finishedPools.length > 0 ? renderPools(finishedPools) : <p className="text-gray-400 mt-4">{t('noFinishedPools')}</p>
          )}
        </>
      )}

      {showCreate && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl p-8 max-w-lg w-full relative">
            <button
              onClick={() => setShowCreate(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
            >
              ✕
            </button>
            <h3 className="text-2xl font-bold text-white mb-4">{t('newPool')}</h3>

            {championships.filter(c => c.status === 'active').length === 0 ? (
              <div className="text-center py-4">
                <p className="text-gray-400 mb-4">{t('noChampionshipsAvailable')}</p>
                <button onClick={() => setShowCreate(false)} className="bg-gray-600 text-white font-bold py-2 px-5 rounded-lg hover:bg-gray-700 transition-colors">
                  {t('close')}
                </button>
              </div>
            ) : (
              <form onSubmit={handleCreatePool}>
                <div className="mb-4">
                  <label className="block text-gray-400 mb-2">{t('poolName')}</label>
                  <input type="text" value={newPoolName} onChange={e => setNewPoolName(e.target.value)} className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" placeholder={t('poolNamePlaceholder')} required />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-400 mb-2">{t('championshipLabel')}</label>
                  <select value={selectedChamp} onChange={e => setSelectedChamp(e.target.value)} className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500">
                    {championships.filter(c => c.status === 'active').map(c => (
                      <option key={c.id} value={c.id}>{t(c.name)}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-gray-400 mb-2">{t('generalRules')}</label>
                  <textarea
                    value={newPoolRules}
                    onChange={e => setNewPoolRules(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-white h-24 resize-y"
                    placeholder={t('rulesPlaceholder')}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-400 mb-2">{t('prizes')}</label>
                  <textarea
                    value={newPoolPrizes}
                    onChange={e => setNewPoolPrizes(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-white h-24 resize-y"
                    placeholder={t('prizesPlaceholder')}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-400 mb-2">Pontuação 2X a partir de:</label>
                  <select 
                    value={newPoolKnockoutMultiplier} 
                    onChange={e => setNewPoolKnockoutMultiplier(e.target.value)} 
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-white"
                  >
                    <option value="Nenhuma">Nenhuma</option>
                    <option value="16 avos">16 avos</option>
                    <option value="Oitavas de Final">Oitavas de Final</option>
                    <option value="Quartas de Final">Quartas de Final</option>
                    <option value="Semifinal">Semifinal</option>
                    <option value="Final">Final</option>
                  </select>
                  <p className="text-xs text-gray-400 mt-1">Multiplica os pontos da partida por 2 a partir da fase selecionada. (Acumula com Partida Bônus)</p>
                </div>
                {user.uid === ADMIN_UID && (
                  <div className="mb-4 p-4 bg-gray-700/50 rounded-lg border border-gray-600">
                    <h4 className="text-white font-bold mb-3 flex items-center gap-2">
                      {t('adminOptions')}
                    </h4>
                    <div className="flex items-center mb-4">
                      <input
                        id="isPublic"
                        type="checkbox"
                        checked={newPoolIsPublic}
                        onChange={e => setNewPoolIsPublic(e.target.checked)}
                        className="w-5 h-5 text-teal-500 rounded focus:ring-teal-500 border-gray-600 bg-gray-700"
                      />
                      <label htmlFor="isPublic" className="ml-2 text-gray-300 font-medium cursor-pointer">
                        {t('publicPoolLabel')}
                      </label>
                    </div>
                    {newPoolIsPublic && (
                      <div>
                        <label className="block text-gray-400 mb-2 text-sm">{t('poolCoverLabel')}</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={e => setNewPoolImage(e.target.files[0])}
                          className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-teal-500 file:text-white hover:file:bg-teal-600 cursor-pointer"
                        />
                        <p className="text-xs text-gray-500 mt-1">Recomendado: 800x400px ou formato 2:1</p>
                      </div>
                    )}
                  </div>
                )}
                <div className="flex justify-end gap-4">
                  <button type="button" onClick={() => setShowCreate(false)} className="bg-gray-600 text-white font-bold py-2 px-5 rounded-lg hover:bg-gray-700 transition-colors">{t('cancel')}</button>
                  <button type="submit" className="bg-green-500 text-white font-bold py-2 px-5 rounded-lg hover:bg-green-600 transition-colors">{t('create')}</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {showJoinModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl p-8 max-w-md w-full">
            <h3 className="text-2xl font-bold text-white mb-4">{t('joinPool')}</h3>
            <form onSubmit={handleSubmitJoinPool}>
              <div className="mb-4">
                <label htmlFor="joinPoolId" className="block text-gray-400 mb-2">{t('poolIdLabel')}</label>
                <input
                  id="joinPoolId"
                  type="text"
                  value={joinPoolIdInput}
                  onChange={(e) => setJoinPoolIdInput(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
                  placeholder={t('pasteIdHere')}
                />
              </div>
              {joinMessage.text && (
                <p className={`p-3 rounded-lg mb-4 text-center ${joinMessage.type === 'error' ? 'bg-red-500/20 text-red-300' : 'bg-green-500/20 text-green-300'}`}>
                  {joinMessage.text}
                </p>
              )}
              <div className="flex justify-end gap-4">
                <button type="button" onClick={() => setShowJoinModal(false)} className="bg-gray-600 text-white font-bold py-2 px-5 rounded-lg hover:bg-gray-700 transition-colors">
                  {t('cancel')}
                </button>
                <button type="submit" className="bg-blue-500 text-white font-bold py-2 px-5 rounded-lg hover:bg-blue-600 transition-colors">
                  Entrar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showUnsubscribeConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl p-8 max-w-md w-full">
            <h3 className="text-2xl font-bold text-white mb-4">Confirmar Cancelamento</h3>
            <p className="text-gray-300 mb-6">Tem certeza de que deseja cancelar sua inscrição neste bolão?</p>
            <div className="flex justify-end gap-4">
              <button onClick={() => setShowUnsubscribeConfirm(false)} className="bg-gray-600 text-white font-bold py-2 px-5 rounded-lg hover:bg-gray-700 transition-colors">
                Não
              </button>
              <button onClick={confirmUnsubscribe} className="bg-red-600 text-white font-bold py-2 px-5 rounded-lg hover:bg-red-700 transition-colors">
                Sim, Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl p-8 max-w-md w-full">
            <h3 className="text-2xl font-bold text-white mb-4">Excluir Bolão</h3>
            <p className="text-gray-300 mb-6">Tem certeza de que deseja excluir este bolão? Esta ação não pode ser desfeita e todos os dados serão perdidos.</p>
            <div className="flex justify-end gap-4">
              <button onClick={() => setShowDeleteConfirm(false)} className="bg-gray-600 text-white font-bold py-2 px-5 rounded-lg hover:bg-gray-700 transition-colors">
                Cancelar
              </button>
              <button onClick={confirmDeletePool} className="bg-red-600 text-white font-bold py-2 px-5 rounded-lg hover:bg-red-700 transition-colors">
                Sim, Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 bg-red-900 text-white rounded-lg my-4">
          <h2 className="text-xl font-bold mb-2">Erro de Renderização na Aba!</h2>
          <p className="font-mono text-sm mb-4">{this.state.error && this.state.error.toString()}</p>
          <button onClick={() => this.setState({ hasError: false })} className="px-4 py-2 bg-red-700 rounded hover:bg-red-600">
            Tentar Novamente
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const PoolView = ({ user, poolId, setView }) => {
  const { t, i18n } = useTranslation();
  const [pool, setPool] = useState(null);
  const [matches, setMatches] = useState([]);
  const [groupedMatches, setGroupedMatches] = useState({});
  const [bets, setBets] = useState({});
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showEditPoolModal, setShowEditPoolModal] = useState(false);
  const [editPoolName, setEditPoolName] = useState('');
  const [editPoolIsPublic, setEditPoolIsPublic] = useState(false);
  const [editPoolImage, setEditPoolImage] = useState(null);

  const handleUpdatePool = async (e) => {
    e.preventDefault();
    if (!pool || !editPoolName || user.uid !== ADMIN_UID) return;

    let photoUrl = pool.photoUrl;

    if (editPoolImage) {
      try {
        const storageRef = ref(storage, `pool-covers/${editPoolImage.name}_${Date.now()}`);
        await uploadBytes(storageRef, editPoolImage);
        photoUrl = await getDownloadURL(storageRef);
      } catch (error) {
        console.error("Erro ao fazer upload da nova imagem:", error);
        alert("Erro ao fazer upload da imagem.");
        return;
      }
    }

    const updates = {
      name: editPoolName,
      isPublic: editPoolIsPublic,
      photoUrl: photoUrl
    };

    try {
      const poolRef = doc(db, "pools", poolId);
      await updateDoc(poolRef, updates);

      // Atualiza estado local se mudou algo visualmente importante (opcional, pois onSnapshot cuida)
      setShowEditPoolModal(false);
      setEditPoolImage(null);
    } catch (error) {
      console.error("Erro ao atualizar bolão:", error);
      alert("Erro ao atualizar bolão.");
    }
  };

  const handleRemovePoolImage = async () => {
    if (!pool || user.uid !== ADMIN_UID) return;
    try {
      const poolRef = doc(db, "pools", poolId);
      await updateDoc(poolRef, { photoUrl: null });
      // Opcional: deletar do storage se quiser limpar lixo
    } catch (error) {
      console.error("Erro ao remover imagem:", error);
    }
  };

  const openEditModal = () => {
    setEditPoolName(pool.name);
    setEditPoolIsPublic(pool.isPublic || false);
    setEditPoolImage(null);
    setShowEditPoolModal(true);
  };
  const [activeTab, setActiveTab] = useState('ranking'); // 'ranking', 'jogos', 'info', 'participantes'
  const [championship, setChampionship] = useState(null);
  const [logoMap, setLogoMap] = useState(new Map());
  const [activePhase, setActivePhase] = useState(null);

  const [showBetsModal, setShowBetsModal] = useState(null);
  const [showBonusBetsModal, setShowBonusBetsModal] = useState(false);
  const [paymentFilter, setPaymentFilter] = useState('todos'); // Filter for participants

  // Estados para F1
  const [f1Drivers, setF1Drivers] = useState([]);
  const [f1Teams, setF1Teams] = useState([]);
  const [seasonBets, setSeasonBets] = useState({});
  const [f1StandingsDrivers, setF1StandingsDrivers] = useState(null);
  const [f1StandingsTeams, setF1StandingsTeams] = useState(null);

  const calculateAndSetRankings = (poolData, allMatches, allBets, champData) => {
    if (!poolData || !poolData.participants) {
      setRankings([]);
      return;
    }

    const finishedMatches = (allMatches || []).filter(m => m.status === 'finished');

    const calculatedRankings = poolData.participants.map(participant => {
      const userBets = (allBets && allBets[participant.uid]) || {};
      let stats = {
        points: 0,
        exactScores: 0,
        winnerHits: 0,
        drawHits: 0,
        bonusPoints: 0,
      };

      if (finishedMatches.length > 0) {
        finishedMatches.forEach(match => {
          const bet = userBets[match.id];
          if (bet) {
            if (match.type === 'f1_race') {
              const { points } = getF1PointsInfo(bet, match);
              stats.points += points;
            } else {
              const { points, type } = getPointsInfo(bet, match, poolData);
              stats.points += points;
              if (bet.isBonus) {
                stats.bonusPoints += points;
              }
              if (type === 'exact') stats.exactScores++;
              if (type === 'winner_goals' || type === 'winner') stats.winnerHits++;
              if (type === 'draw') stats.drawHits++;
            }
          }
        });
      }

      // Cálculo de Bônus de Temporada
      if (champData) {
        const sBets = userBets.seasonBets || {};
        if (champData.type === 'Formula 1') {
          if (champData.championDriver && sBets.championDriver === champData.championDriver) {
            stats.points += 25;
            stats.bonusPoints += 25;
          }
          if (champData.championTeam && sBets.championTeam === champData.championTeam) {
            stats.points += 25;
            stats.bonusPoints += 25;
          }
        } else {
          if (champData.resultChampion && sBets.champion === champData.resultChampion) {
            stats.points += 50;
            stats.bonusPoints += 50;
          }
          if (champData.resultVice && sBets.vice === champData.resultVice) {
            stats.points += 30;
            stats.bonusPoints += 30;
          }
          const isTopScorer1 = champData.resultTopScorerTeam && sBets.topScorerTeam === champData.resultTopScorerTeam;
          const isTopScorer2 = champData.resultTopScorerTeam2 && sBets.topScorerTeam === champData.resultTopScorerTeam2;
          const isTopScorer3 = champData.resultTopScorerTeam3 && sBets.topScorerTeam === champData.resultTopScorerTeam3;
          if (isTopScorer1 || isTopScorer2 || isTopScorer3) {
            stats.points += 50;
            stats.bonusPoints += 50;
          }
        }
      }

      return {
        uid: participant.uid,
        displayName: participant.displayName,
        status: participant.status || 'active',
        ...stats
      };
    });

    // Ordena com critérios de desempate
    calculatedRankings.sort((a, b) => {
      if (b.points !== a.points) return b.points - a.points;
      if (b.exactScores !== a.exactScores) return b.exactScores - a.exactScores;
      if (b.winnerHits !== a.winnerHits) return b.winnerHits - a.winnerHits;
      if (b.drawHits !== a.drawHits) return b.drawHits - a.drawHits;
      if (b.bonusPoints !== a.bonusPoints) return b.bonusPoints - a.bonusPoints;
      return (a.displayName || "Sem nome").localeCompare(b.displayName || "Sem nome");
    });

    setRankings(calculatedRankings);
  };

  useEffect(() => {
    if (!poolId) return;

    setLoading(true);
    const unsubscribers = [];

    const poolRef = doc(db, "pools", poolId);
    const unsubPool = onSnapshot(poolRef, (poolDoc) => {
      if (!poolDoc.exists()) {
        console.error("Bolão não encontrado!");
        setPool(null);
        setLoading(false);
        return;
      }

      const poolData = { id: poolDoc.id, ...poolDoc.data() };
      setPool(poolData);

      if (!poolData.championshipId) {
        setLoading(false);
        return;
      }

      // Busca dados do campeonato para obter os logos
      let currentChampData = null;
      const champRef = doc(db, "championships", poolData.championshipId);
      const unsubChamp = onSnapshot(champRef, (champDoc) => {
        if (champDoc.exists()) {
          const champData = champDoc.data();
          setChampionship(champData);
          const newLogoMap = new Map();
          if (champData.type === 'Liga' && champData.teams) {
            champData.teams.forEach(team => {
              if (typeof team === 'object' && team.name) {
                newLogoMap.set(team.name, team.logoUrl || null);
              }
            });
          } else if (champData.type === 'Copa' && champData.groups) {
            champData.groups.forEach(group => {
              (group.teams || []).forEach(team => {
                if (typeof team === 'object' && team.name) {
                  newLogoMap.set(team.name, team.logoUrl || null);
                }
              });
            });
          }
          setLogoMap(newLogoMap);
          currentChampData = champData;
          calculateAndSetRankings(poolData, currentMatches, currentAllBets, currentChampData);
        } else {
          console.warn("Campeonato não encontrado para ID:", poolData.championshipId);
          setChampionship({ name: 'Campeonato (Não encontrado)' });
        }
      });
      unsubscribers.push(unsubChamp);

      let currentMatches = [];
      let currentAllBets = {};

      const matchesQuery = query(collection(db, "matches"), where("championshipId", "==", poolData.championshipId));
      const unsubMatches = onSnapshot(matchesQuery, (matchesSnapshot) => {
        currentMatches = matchesSnapshot.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => {
          const aTs = a.matchDate && a.matchDate.seconds ? a.matchDate.seconds : 0;
          const bTs = b.matchDate && b.matchDate.seconds ? b.matchDate.seconds : 0;
          return aTs - bTs;
        });

        const grouped = currentMatches.reduce((acc, match) => {
          const phase = match.phase || 'Jogos Gerais';
          if (!acc[phase]) acc[phase] = [];
          acc[phase].push(match);
          return acc;
        }, {});

        setMatches(currentMatches);
        setGroupedMatches(grouped);

        // const phases = Object.keys(grouped).sort(); // Linha antiga
        const phases = Object.keys(grouped).sort((a, b) => getPhaseSortValue(a) - getPhaseSortValue(b)); // Linha nova
        if (phases.length > 0 && !activePhase) {
          setActivePhase(phases[0]);
        }

        calculateAndSetRankings(poolData, currentMatches, currentAllBets, currentChampData);
        setLoading(false);
      });
      unsubscribers.push(unsubMatches);

      const betsQuery = query(collection(db, "pools", poolId, "bets"));
      const unsubBets = onSnapshot(betsQuery, (betsSnapshot) => {
        currentAllBets = {};
        betsSnapshot.forEach((betDoc) => {
          currentAllBets[betDoc.id] = betDoc.data();
        });

        setBets(currentAllBets);
        calculateAndSetRankings(poolData, currentMatches, currentAllBets, currentChampData);
        setLoading(false);
      });
      unsubscribers.push(unsubBets);
    });
    unsubscribers.push(unsubPool);

    return () => {
      unsubscribers.forEach(unsub => {
        try { unsub(); } catch (e) { /* ignore */ }
      });
      setActivePhase(null); // Reset on component unmount/poolId change
    };
    // Depend only on poolId to avoid re-subscribing continuously

  }, [poolId]);

  // Busca dados de F1 se for o caso
  useEffect(() => {
    if (championship?.type === 'Formula 1') {
      const fetchF1Data = async () => {
        try {
          const driversQ = query(collection(db, "f1_drivers"));
          const driversSnap = await getDocs(driversQ);
          const driversData = driversSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          driversData.sort((a, b) => a.name.localeCompare(b.name));
          setF1Drivers(driversData);

          const teamsQ = query(collection(db, "f1_teams"));
          const teamsSnap = await getDocs(teamsQ);
          const teamsData = teamsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          teamsData.sort((a, b) => a.name.localeCompare(b.name));
          setF1Teams(teamsData);

          const driversStandingsRef = doc(db, "f1_standings_drivers", "2026");
          const driversStandingsSnap = await getDoc(driversStandingsRef);
          if (driversStandingsSnap.exists()) {
             setF1StandingsDrivers(driversStandingsSnap.data().data);
          }

          const teamsStandingsRef = doc(db, "f1_standings_teams", "2026");
          const teamsStandingsSnap = await getDoc(teamsStandingsRef);
          if (teamsStandingsSnap.exists()) {
             setF1StandingsTeams(teamsStandingsSnap.data().data);
          }
        } catch (err) {
          console.error("Erro ao buscar dados F1:", err);
        }
      };
      fetchF1Data();
    }
  }, [championship]);

  // Carrega palpites da temporada (Bônus)
  useEffect(() => {
    if (poolId && user && championship) {
      const fetchSeasonBets = async () => {
        const betRef = doc(db, "pools", poolId, "bets", user.uid);
        const betSnap = await getDoc(betRef);
        if (betSnap.exists() && betSnap.data().seasonBets) {
          setSeasonBets(betSnap.data().seasonBets);
        }
      };
      fetchSeasonBets();
    }
  }, [poolId, user, championship]);


  const handleBetChange = async (matchId, team, value) => {
    const match = matches.find(m => m.id === matchId);
    if (match) {
      const matchDate = match.matchDate && match.matchDate.seconds ? new Date(match.matchDate.seconds * 1000) : getRealDate();
      const deadline = new Date(matchDate.getTime() - 2 * 60000); // 2 minutos de trava
      if (getRealDate() > deadline) {
        alert(t ? t('locked') : 'Este jogo já está bloqueado para apostas.');
        return;
      }
    }

    const currentBets = bets[user.uid] || {};
    const newBet = {
      ...currentBets[matchId],
      [team]: value
    };

    const updatedBets = { ...currentBets, [matchId]: newBet };

    setBets(prev => ({ ...prev, [user.uid]: updatedBets }));

    const betRef = doc(db, "pools", poolId, "bets", user.uid);

    await setDoc(betRef, { [matchId]: newBet }, { merge: true });
  };

  const handleSeasonBetChange = async (field, value) => {
    const newSeasonBets = { ...seasonBets, [field]: value };
    setSeasonBets(newSeasonBets);

    const betRef = doc(db, "pools", poolId, "bets", user.uid);
    await setDoc(betRef, { seasonBets: newSeasonBets }, { merge: true });
  };

  const handleBonusToggle = async (toggledMatchId, phase) => {
    const match = matches.find(m => m.id === toggledMatchId);
    if (match) {
      const matchDate = match.matchDate && match.matchDate.seconds ? new Date(match.matchDate.seconds * 1000) : getRealDate();
      const deadline = new Date(matchDate.getTime() - 2 * 60000); // 2 minutos de trava
      if (getRealDate() > deadline) {
        alert(t ? t('locked') : 'Este jogo já está bloqueado para apostas.');
        return;
      }
    }

    const userBets = bets[user.uid] || {};
    const updates = {};
    const isTogglingOff = userBets[toggledMatchId]?.isBonus === true;

    // Limpa qualquer bônus existente na mesma fase
    (groupedMatches[phase] || []).forEach(match => {
      if (userBets[match.id]?.isBonus) {
        updates[match.id] = { ...userBets[match.id], isBonus: false };
      }
    });

    // Define o novo bônus, a menos que estejamos desativando o atual
    if (!isTogglingOff) {
      updates[toggledMatchId] = { ...(userBets[toggledMatchId] || {}), isBonus: true };
    }

    const newBetsForUser = { ...userBets, ...updates };
    setBets(prev => ({ ...prev, [user.uid]: newBetsForUser }));

    const betRef = doc(db, "pools", poolId, "bets", user.uid);
    await setDoc(betRef, updates, { merge: true });
  };

  const copyInviteLink = async () => {
    let baseUrl = window.location.origin;
    if (baseUrl.includes('localhost') || baseUrl.includes('127.0.0.1')) {
      baseUrl = 'https://boloestop.com.br';
    }
    const inviteURL = `${baseUrl}${window.location.pathname}?join=${poolId}`;

    const poolName = pool?.name || t('pool');
    const champName = championship?.name ? t(championship.name) : t('championship');

    const message = t('shareInviteMessage', { poolName, champName, inviteURL });

    // Tenta API moderna primeiro
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(message);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        return;
      } catch (err) {
        console.warn('API clipboard moderna falhou, tentando fallback', err);
      }
    }

    // Fallback legado
    const el = document.createElement('textarea');
    el.value = message;
    el.setAttribute('readonly', '');
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.select();
    try {
      document.execCommand('copy');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error(t('copyLinkError'), err);
    }
    document.body.removeChild(el);
  };

  const shareInviteLink = async () => {
    let baseUrl = window.location.origin;
    if (baseUrl.includes('localhost') || baseUrl.includes('127.0.0.1')) {
      baseUrl = 'https://boloestop.com.br';
    }
    const inviteURL = `${baseUrl}${window.location.pathname}?join=${poolId}`;

    const poolName = pool?.name || t('pool');
    const champName = championship?.name ? t(championship.name) : t('championship');

    const message = t('shareInviteMessage', { poolName, champName, inviteURL });

    try {
      if (Capacitor.isNativePlatform()) {
        await Share.share({
          title: t('shareInviteTitle', { poolName }),
          text: message,
          dialogTitle: t('shareInviteDialogTitle')
        });
      } else if (navigator.share && window.isSecureContext) {
        await navigator.share({
          title: t('shareInviteTitle', { poolName }),
          text: message,
        });
      } else {
        copyInviteLink();
      }
    } catch (err) {
      console.error(t('shareError'), err);
    }
  };

  const handleToggleParticipantStatus = async (participantToUpdate) => {
    if (user.uid !== pool.adminId) return;

    const newStatus = (participantToUpdate.status === 'inactive') ? 'active' : 'inactive';

    const updatedParticipants = pool.participants.map(p => {
      if (p.uid === participantToUpdate.uid) {
        return { ...p, status: newStatus };
      }
      return p;
    });

    try {
      const poolRef = doc(db, "pools", poolId);
      await updateDoc(poolRef, {
        participants: updatedParticipants
      });
    } catch (error) {
      console.error("Erro ao atualizar status do participante:", error);
    }
  };

  const handleTogglePaymentStatus = async (participantToUpdate) => {
    if (user.uid !== pool.adminId) return;

    const newPaidStatus = !participantToUpdate.paid;

    const updatedParticipants = pool.participants.map(p => {
      if (p.uid === participantToUpdate.uid) {
        return { ...p, paid: newPaidStatus };
      }
      return p;
    });

    try {
      const poolRef = doc(db, "pools", poolId);
      await updateDoc(poolRef, {
        participants: updatedParticipants
      });
    } catch (error) {
      console.error("Erro ao atualizar status de pagamento:", error);
    }
  };

  // const sortedPhases = useMemo(() => Object.keys(groupedMatches).sort(), [groupedMatches]); // Linha antiga
  const sortedPhases = useMemo(() => { // Bloco novo
    return Object.keys(groupedMatches).sort((a, b) => getPhaseSortValue(a) - getPhaseSortValue(b));
  }, [groupedMatches]);

  // HOOKS MOVIDOS PARA ANTES DOS RETURNS PARA CORRIGIR ERRO DE ORDEM
  const userBets = useMemo(() => bets[user.uid] || {}, [bets, user.uid]);

  // Verifica se já existe um bônus ativo nesta fase
  const hasBonusInThisPhase = useMemo(() => {
    if (!activePhase || !groupedMatches[activePhase] || !userBets) return false;

    const matchesInPhase = groupedMatches[activePhase];
    // Verifica se algum palpite para os jogos desta fase já tem o bônus
    return matchesInPhase.some(match => userBets[match.id]?.isBonus === true);
  }, [activePhase, groupedMatches, userBets]);

  const currentPhaseIndex = sortedPhases.indexOf(activePhase);

  const handleNextPhase = () => {
    if (currentPhaseIndex < sortedPhases.length - 1) {
      setActivePhase(sortedPhases[currentPhaseIndex + 1]);
    }
  };

  const handlePrevPhase = () => {
    if (currentPhaseIndex > 0) {
      setActivePhase(sortedPhases[currentPhaseIndex - 1]);
    }
  };

  if (loading) return <div className="p-8 text-white flex items-center justify-center"><Icon name="spinner" /> {t('loadingPool')}</div>;
  if (!pool) return <div className="p-8 text-white">{t('poolLoadError')}</div>;

  // const userBets = bets[user.uid] || {}; // MOVIDO PARA CIMA (useMemo)

  // const hasBonusInThisPhase = useMemo(() => { ... }); // MOVIDO PARA CIMA

  const TabButton = ({ tabName, label }) => {
    const isActive = activeTab === tabName;
    return (
      <button
        onClick={() => setActiveTab(tabName)}
        className={`py-2 px-4 font-semibold transition-colors duration-300 whitespace-nowrap shrink-0 ${isActive ? 'text-teal-400 border-b-2 border-teal-400' : 'text-gray-400 hover:text-white'}`}
      >
        {label}
      </button>
    );
  };




  return (
    <div className="p-4 md:p-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div className="md:flex-1 flex justify-start">
          <button onClick={() => setView('dashboard')} className="flex items-center text-teal-400 hover:text-teal-300">
            <Icon name="arrowLeft" />
            <span className="sm:inline ml-1">{t('backToDashboard')}</span>
          </button>
        </div>
        <h2 className="md:flex-[2] text-xl sm:text-3xl font-bold text-white text-center">
          {pool.name}
        </h2>
        <div className="md:flex-1 flex gap-2 justify-center md:justify-end">
          {user.uid === ADMIN_UID && (
            <button onClick={openEditModal} className="bg-blue-600 text-white font-bold py-2 px-3 sm:px-4 rounded-lg hover:bg-blue-500 transition-colors flex items-center text-sm sm:text-base">
              <Icon name="edit" /> <span className="ml-1 sm:ml-2">{t('edit')}</span>
            </button>
          )}
          {user.uid === pool.adminId && (
            <div className="flex gap-2">
              <button 
                onClick={copyInviteLink} 
                className="flex bg-gray-700 text-white font-bold py-2 px-3 sm:px-4 rounded-lg hover:bg-gray-600 transition-colors items-center text-sm sm:text-base"
                title="Copiar Link"
              >
                <Icon name={copied ? "check" : "copy"} /> <span className="hidden md:inline ml-1 sm:ml-2">{copied ? t('copied') : t('invite')}</span>
              </button>
              <button 
                onClick={shareInviteLink} 
                className="flex md:hidden bg-green-600 border border-green-500 text-white font-bold py-2 px-3 rounded-lg hover:bg-green-500 transition-colors items-center text-sm"
              >
                <Icon name="share" /> <span className="ml-1">{t('share')}</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="border-b border-gray-700 mb-6 w-full overflow-hidden">
        <nav className="flex space-x-4 overflow-x-auto scrollbar-hide pb-1">
          <TabButton tabName="ranking" label={t('tabRanking')} />

          <TabButton tabName="jogos" label={championship?.type === 'Formula 1' ? t('tabRaces') : t('tabGames')} />
          <TabButton tabName="bonus" label={t('tabBonus')} />
          {championship?.type === 'Formula 1' && (
            <>
              <TabButton tabName="f1_drivers_standings" label={t('tabDriversStandings')} />
              <TabButton tabName="f1_teams_standings" label={t('tabTeamsStandings')} />
            </>
          )}
          <TabButton tabName="info" label={t('tabInfo')} />
          <TabButton tabName="participantes" label={t('tabParticipants')} />
        </nav>
      </div>

      <div className="mt-4">
        <ErrorBoundary>
        {activeTab === 'ranking' && (
          <div className="bg-gray-800/50 rounded-xl p-6">
            <h3 className="text-2xl font-bold text-white mb-4 flex items-center"><Icon name="trophy" />{t('tabRanking')}</h3>
            <ul className="space-y-3">
              {rankings.map((p, index) => (
                <li key={p.uid} className={`flex justify-between items-center bg-gray-700/50 p-3 rounded-lg ${p.status === 'inactive' ? 'opacity-50' : ''}`}>
                  <div className="flex items-center">
                    <span className={`font-bold w-8 text-center ${index < 3 ? 'text-yellow-400' : 'text-gray-400'}`}>{index + 1}.</span>
                    <span className="text-white ml-3">{p.displayName || "Participante"} {p.status === 'inactive' && <span className="text-xs text-red-400">(Inativo)</span>}</span>
                  </div>
                  <span className="font-bold text-teal-400">{p.points} pts</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {activeTab === 'jogos' && (
          <div className="bg-gray-800/50 rounded-xl p-6">
            {sortedPhases.length > 1 && (
              <div className="flex items-center justify-between mb-6 bg-gray-900/50 p-3 rounded-lg">
                <button
                  onClick={handlePrevPhase}
                  disabled={currentPhaseIndex === 0}
                  className="p-2 rounded-full disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-700 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
                </button>
                <h4 className="text-xl font-semibold text-teal-300 text-center">
                  {activePhase.replace('Rodada', t('round')).replace('Fase', t('phase'))}
                </h4>
                <button
                  onClick={handleNextPhase}
                  disabled={currentPhaseIndex === sortedPhases.length - 1}
                  className="p-2 rounded-full disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-700 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>
            )}
            <div className="space-y-4 pr-2">
              {activePhase && (groupedMatches[activePhase] || []).length > 0 ? (
                (groupedMatches[activePhase] || []).map(match => {
                  const matchDate = match.matchDate && match.matchDate.seconds ? new Date(match.matchDate.seconds * 1000) : getRealDate();
                  const deadline = new Date(matchDate.getTime() - 2 * 60000); // 2 minutos de trava
                  const isLocked = getRealDate() > deadline;
                  const isFinished = match.status === 'finished';
                  const isThisTheBonus = userBets[match.id]?.isBonus;
                  const pointsInfo = isFinished ? getPointsInfo(userBets[match.id] || {}, match, pool) : null;

                  const logoA = logoMap.get(match.teamA);
                  const logoB = logoMap.get(match.teamB);

                  // NOVO: Verifica se o jogo teve prorrogação/pênaltis
                  const hasExtraTime = (match.resultA_ext !== null && match.resultA_ext !== undefined) && (match.resultB_ext !== null && match.resultB_ext !== undefined);

                  // NOVO: Define o texto do resultado com base na verificação
                  const resultLabel = hasExtraTime ? t('resultNormalTime') : t('result');



                  if (championship?.type === 'Formula 1') {
                    return (
                      <div key={match.id} className={`p-4 rounded-lg mb-4 relative ${isFinished ? 'bg-gray-700/60' : 'bg-gray-700'}`}>
                        <div className="flex justify-between items-center mb-4">
                          <div>
                            <span className="font-bold text-lg text-white">{match.country}</span>
                            <p className="text-sm text-gray-400">{match.circuit}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-400">{matchDate.toLocaleDateString(i18n.language)} - {matchDate.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}</p>
                            {isLocked && !isFinished && <span className="text-xs font-bold text-yellow-400 bg-yellow-500/10 px-2 py-1 rounded-full">{t('locked')}</span>}
                            {isFinished && <span className="text-xs font-bold text-teal-400 bg-teal-500/10 px-2 py-1 rounded-full">{t('finished')}</span>}
                          </div>
                        </div>

                        {isFinished && (
                          <div className="mb-4 bg-gray-800/50 p-3 rounded-lg border border-gray-600">
                            {(() => {
                              const scoreDetails = getF1ScoreDetails(userBets[match.id], match);
                              if (scoreDetails) {
                                return (
                                  <p className="text-teal-300 font-bold text-center">
                                    {scoreDetails.points} {t('pointsCount')} <span className="text-sm font-normal text-gray-300">({scoreDetails.breakdown})</span>
                                  </p>
                                );
                              } else {
                                  return <p className="text-gray-400 text-center text-sm">{t('waitingProcessing')}</p>;
                              }
                            })()}
                          </div>
                        )}

                        {/* --- NOVO BOTÃO ADICIONADO PARA F1 --- */}
                        {isLocked && (
                          <div className="mb-4 text-center">
                            <button
                              onClick={() => setShowBetsModal(match.id)}
                              className="text-sm text-teal-300 hover:text-teal-200 font-semibold transition-colors"
                            >
                              {t('viewAllBets')}
                            </button>
                          </div>
                        )}
                        {/* --- FIM DO NOVO BOTÃO --- */}

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {['p1', 'p2', 'p3'].map((pos, idx) => (
                            <div key={pos}>
                              <label className="block text-xs text-gray-400 mb-1">{t('ordinalPos', { pos: idx + 1 })}</label>
                              <F1CustomSelect
                                value={userBets[match.id]?.[pos] || ''}
                                onChange={(val) => handleBetChange(match.id, pos, val)}
                                disabled={isLocked || isFinished}
                                placeholder={t('select')}
                                options={f1Drivers.map(driver => {
                                  const team = f1Teams.find(t => t.id === driver.teamId) || f1Teams.find(t => t.name === driver.teamName);
                                  return {
                                    ...driver,
                                    teamLogo: team?.logo
                                  };
                                })}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={match.id} className={`p-4 rounded-lg mb-4 relative ${isFinished ? 'bg-gray-700/60' : 'bg-gray-700'}`}>
                      <div className="flex justify-between items-center mb-2">
                        <p className="text-sm text-gray-400">{matchDate.toLocaleDateString(i18n.language)} - {matchDate.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}</p>
                        <div>
                          {isLocked && !isFinished && <span className="text-xs font-bold text-yellow-400 bg-yellow-500/10 px-2 py-1 rounded-full">{t('locked')}</span>}
                          {isFinished && <span className="text-xs font-bold text-teal-400 bg-teal-500/10 px-2 py-1 rounded-full ml-2">{t('finished')}</span>}
                        </div>
                      </div>
                      <div className="grid grid-cols-12 gap-1 sm:gap-2 items-center text-white">
                        <div className="col-span-4 sm:col-span-5 text-right">
                          <div className="flex flex-col items-center">
                            {logoA && <img src={logoA} alt={match.teamA} className="w-8 h-8 sm:w-10 sm:h-10 object-contain mb-1" />}
                            <span className="font-bold text-xs sm:text-lg leading-tight text-center">{match.teamA}</span>
                          </div>
                        </div>
                        <div className="col-span-4 sm:col-span-2 flex justify-center items-center gap-1 sm:gap-2">
                          <input
                            type="number"
                            min="0"
                            disabled={isLocked || isFinished}
                            value={userBets[match.id]?.betA ?? ''}
                            onChange={e => handleBetChange(match.id, 'betA', e.target.value)}
                            className="w-10 sm:w-12 text-center bg-gray-800 rounded-md p-1 sm:p-2 border border-gray-600 disabled:bg-gray-600/50 text-sm sm:text-base"
                          />
                          <span className="text-gray-400">x</span>
                          <input
                            type="number"
                            min="0"
                            disabled={isLocked || isFinished}
                            value={userBets[match.id]?.betB ?? ''}
                            onChange={e => handleBetChange(match.id, 'betB', e.target.value)}
                            className="w-10 sm:w-12 text-center bg-gray-800 rounded-md p-1 sm:p-2 border border-gray-600 disabled:bg-gray-600/50 text-sm sm:text-base"
                          />
                        </div>
                        <div className="col-span-4 sm:col-span-5">
                          <div className="flex flex-col items-center">
                            {logoB && <img src={logoB} alt={match.teamB} className="w-8 h-8 sm:w-10 sm:h-10 object-contain mb-1" />}
                            <span className="font-bold text-xs sm:text-lg leading-tight text-center">{match.teamB}</span>
                          </div>
                        </div>
                      </div>
                      {isFinished && pointsInfo && (
                        <div className="text-center mt-2">
                          <p className="text-gray-300">{resultLabel} <span className="font-bold text-white">{match.resultA} x {match.resultB}</span></p>
                          {/* A condição aqui já estava correta, apenas usando a nova variável */}
                          {hasExtraTime && (
                            <p className="text-sm text-gray-400">
                              {t('finalScore', { method: match.extraTimeMethod ? (match.extraTimeMethod === 'Prorrogação' ? t('overtime') : match.extraTimeMethod) : t('overtime') })} <span className="font-semibold text-gray-200">{match.resultA_ext} x {match.resultB_ext}</span>
                            </p>
                          )}
                          <p className="text-teal-300">
                            {t('yourScore')} <span className="font-bold">{pointsInfo.points}</span>
                            {isThisTheBonus && ` (${pointsInfo.rawPoints} x 2)`}
                          </p>
                        </div>
                      )}

                      {/* --- NOVO BOTÃO ADICIONADO --- */}
                      {/* Mostra o botão se os palpites estiverem encerrados (isLocked) */}
                      {isLocked && (
                        <div className="mt-4 pt-3 border-t border-gray-600/50 text-center">
                          <button
                            onClick={() => setShowBetsModal(match.id)}
                            className="text-sm text-teal-300 hover:text-teal-200 font-semibold transition-colors"
                          >
                            {t('viewAllBets')}
                          </button>
                        </div>
                      )}
                      {/* --- FIM DO NOVO BOTÃO --- */}

                      {/* LÓGICA ATUALIZADA:
                          Mostra o botão de bônus se:
                          1. O jogo não terminou E
                          2. O jogo não está travado E
                          3. ( (Não existe bônus nesta fase) OU (Este é o jogo bônus, permitindo desmarcar) )
                      */}
                      {!isFinished && !isLocked && (!hasBonusInThisPhase || isThisTheBonus) && (
                        <div className="absolute top-2 right-2">
                          <button
                            onClick={() => handleBonusToggle(match.id, activePhase)}
                            className={`px-2 py-1 text-xs font-semibold rounded-full transition-all duration-200 flex items-center ${isThisTheBonus ? 'bg-yellow-500 text-gray-900' : 'bg-gray-600 text-yellow-400 hover:bg-gray-500'}`}
                            title={isThisTheBonus ? t('bonusMatchActivated') : t('activateBonus')}
                          >
                            ⭐
                          </button>
                        </div>
                      )}
                      {(isLocked || isFinished) && isThisTheBonus && (
                        <div className="absolute top-2 right-2">
                          <span className="text-yellow-400 text-2xl" title="Partida Bônus">⭐</span>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (<p className="text-gray-400 text-center">{t('noMatchesFound')}</p>)
              }
            </div>
          </div>
        )}

        {activeTab === 'bonus' && (
          <div className="bg-gray-800/50 rounded-xl p-6">
            <h3 className="text-2xl font-bold text-white mb-6">{t('seasonPredictions')}</h3>
            {(() => {
              // Lógica de travamento: 2 minutos antes da primeira partida/corrida ou data customizada
              const sortedMatches = matches.length > 0 ? [...matches].sort((a, b) => (a.matchDate?.seconds || 0) - (b.matchDate?.seconds || 0)) : [];
              const firstMatch = sortedMatches[0];
              const firstMatchDate = firstMatch?.matchDate?.seconds ? new Date(firstMatch.matchDate.seconds * 1000) : getRealDate();
              
              let customDeadline = pool?.customBonusDeadline?.seconds ? new Date(pool.customBonusDeadline.seconds * 1000) : null;
              
              // Fallback específico para o bolão "Futs Quinta Copa26"
              if (!customDeadline && poolId === 'VhSuufKiuOpmTdrp82Vr') {
                customDeadline = new Date('2026-07-04T13:58:00-03:00');
              }
              
              const deadline = customDeadline || new Date(firstMatchDate.getTime() - 2 * 60000);
              const isLocked = matches.length > 0 ? (getRealDate() > deadline) : false;

              if (championship?.type === 'Formula 1') {
                return (
                  <div className="space-y-6">
                    {isLocked && (
                      <div className="bg-yellow-500/10 border border-yellow-500/50 text-yellow-200 p-4 rounded-lg mb-4">
                        {t('bonusPredictionsClosed')}
                      </div>
                    )}
                    <div>
                      <label className="block text-gray-300 mb-2 font-semibold">{t('championDriver')}</label>
                      <F1CustomSelect
                        value={seasonBets.championDriver || ''}
                        onChange={(val) => handleSeasonBetChange('championDriver', val)}
                        disabled={isLocked}
                        placeholder={t('selectDriver')}
                        options={f1Drivers.map(driver => {
                          const team = f1Teams.find(t => t.id === driver.teamId) || f1Teams.find(t => t.name === driver.teamName);
                          return {
                            ...driver,
                            teamLogo: team?.logo
                          };
                        })}
                      />
                      {championship?.championDriver && (
                        <p className="mt-2 text-sm text-teal-400 font-semibold">
                          {t('result')} {championship.championDriver}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2 font-semibold">{t('championTeam')}</label>
                      <F1CustomSelect
                        value={seasonBets.championTeam || ''}
                        onChange={(val) => handleSeasonBetChange('championTeam', val)}
                        disabled={isLocked}
                        placeholder={t('selectTeam')}
                        options={f1Teams.map(team => ({
                          id: team.id,
                          name: team.name,
                          photo: team.logo,
                          teamName: null,
                          teamLogo: null
                        }))}
                      />
                      {championship?.championTeam && (
                        <p className="mt-2 text-sm text-teal-400 font-semibold">
                          {t('result')} {championship.championTeam}
                        </p>
                      )}
                    </div>
                    {isLocked && (
                      <div className="mt-6 pt-6 border-t border-gray-700 text-center">
                        <button
                          onClick={() => setShowBonusBetsModal(true)}
                          className="text-sm text-teal-300 hover:text-teal-200 font-semibold transition-colors"
                        >
                          {t('viewAllBets')}
                        </button>
                      </div>
                    )}
                  </div>
                );
              } else {
                // Lógica para Futebol
                const teams = [];
                if (championship?.type === 'Liga') {
                  (championship.teams || []).forEach(t => teams.push(t));
                } else if (championship?.type === 'Copa') {
                  (championship.groups || []).forEach(g => {
                    (g.teams || []).forEach(t => teams.push(t));
                  });
                }
                const sortedTeams = teams.sort((a, b) => a.name.localeCompare(b.name));

                return (
                  <div className="space-y-6">
                    {isLocked && (
                      <div className="bg-yellow-500/10 border border-yellow-500/50 text-yellow-200 p-4 rounded-lg mb-4">
                        {t('bonusPredictionsClosed')}
                      </div>
                    )}
                    <div>
                      <label className="block text-gray-300 mb-2 font-semibold">{t('champion')}</label>
                      <select
                        value={seasonBets.champion || ''}
                        onChange={(e) => handleSeasonBetChange('champion', e.target.value)}
                        disabled={isLocked}
                        className="w-full bg-gray-700 p-3 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-teal-500 text-white"
                      >
                        <option value="">{t('select')}</option>
                        {sortedTeams.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
                      </select>
                      {championship?.resultChampion && (
                        <p className="mt-2 text-sm text-teal-400 font-semibold">
                          {t('result')} {championship.resultChampion}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2 font-semibold">{t('viceChampion')}</label>
                      <select
                        value={seasonBets.vice || ''}
                        onChange={(e) => handleSeasonBetChange('vice', e.target.value)}
                        disabled={isLocked}
                        className="w-full bg-gray-700 p-3 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-teal-500 text-white"
                      >
                        <option value="">{t('select')}</option>
                        {sortedTeams.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
                      </select>
                      {championship?.resultVice && (
                        <p className="mt-2 text-sm text-teal-400 font-semibold">
                          {t('result')} {championship.resultVice}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2 font-semibold">{t('topScorerTeam')}</label>
                      <select
                        value={seasonBets.topScorerTeam || ''}
                        onChange={(e) => handleSeasonBetChange('topScorerTeam', e.target.value)}
                        disabled={isLocked}
                        className="w-full bg-gray-700 p-3 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-teal-500 text-white"
                      >
                        <option value="">{t('select')}</option>
                        {sortedTeams.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
                      </select>
                      {(championship?.resultTopScorerTeam || championship?.resultTopScorerTeam2 || championship?.resultTopScorerTeam3) && (
                        <p className="mt-2 text-sm text-teal-400 font-semibold">
                          {t('result')} {[championship.resultTopScorerTeam, championship.resultTopScorerTeam2, championship.resultTopScorerTeam3].filter(Boolean).join(', ')}
                        </p>
                      )}
                    </div>
                    {isLocked && (
                      <div className="mt-6 pt-6 border-t border-gray-700 text-center">
                        <button
                          onClick={() => setShowBonusBetsModal(true)}
                          className="text-sm text-teal-300 hover:text-teal-200 font-semibold transition-colors"
                        >
                          {t('viewAllBets')}
                        </button>
                      </div>
                    )}
                  </div>
                );
              }
            })()}
          </div>
        )}

        {activeTab === 'info' && (
          <div className="bg-gray-800/50 rounded-xl p-6 text-gray-300">
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-white mb-4">{t('generalRules')}</h3>
              <div className="prose prose-invert max-w-none">
                <p className="whitespace-pre-wrap">{pool.rules || t('noGeneralRules')}</p>
              </div>
            </div>
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-white mb-4">{t('prizesTitle')}</h3>
              <div className="prose prose-invert max-w-none">
                <p className="whitespace-pre-wrap">{pool.prizes || t('noPrizesDefined')}</p>
              </div>
            </div>
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-white mb-4">{t('scoringSystem')}</h3>
              {championship?.type === 'Formula 1' ? (
                <ul className="list-disc list-inside space-y-2">
                  <li><span className="font-bold text-white">{t('pointsOrdinal', { points: 10 })}</span> {t('f1Pos1')}</li>
                  <li><span className="font-bold text-white">{t('pointsOrdinal', { points: 6 })}</span> {t('f1Pos2')}</li>
                  <li><span className="font-bold text-white">{t('pointsOrdinal', { points: 3 })}</span> {t('f1Pos3')}</li>
                  <li><span className="font-bold text-yellow-400">{t('extraPoint')}</span> {t('exactOrder')}</li>
                  <li className="mt-4 font-bold text-teal-400">{t('seasonBonus')}</li>
                  <li><span className="font-bold text-white">{t('pointsOrdinal', { points: 25 })}</span> {t('championDriverPoints')}</li>
                  <li><span className="font-bold text-white">{t('pointsOrdinal', { points: 25 })}</span> {t('championTeamPoints')}</li>
                </ul>
              ) : (
                <ul className="list-disc list-inside space-y-2">
                  <li><span className="font-bold text-white">{t('pointsOrdinal', { points: 10 })}</span> {t('exactScorePoints')}</li>
                  <li><span className="font-bold text-white">{t('pointsOrdinal', { points: 6 })}</span> {t('winnerAndGoalsPoints')}</li>
                  <li><span className="font-bold text-white">{t('pointsOrdinal', { points: 5 })}</span> {t('drawPoints')}</li>
                  <li><span className="font-bold text-white">{t('pointsOrdinal', { points: 3 })}</span> {t('winnerOnlyPoints')}</li>
                  <li><span className="font-bold text-yellow-400">{t('bonusMatch')}</span> {t('bonusMatchDesc')}</li>
                  <li><span className="font-bold text-yellow-400">{t('penaltiesTitle')}</span> {t('penaltiesDesc')}</li>
                  <li className="mt-4 font-bold text-teal-400">{t('seasonBonus')}</li>
                  <li><span className="font-bold text-white">{t('pointsOrdinal', { points: 50 })}</span> {t('bonusPointsChampion')}</li>
                  <li><span className="font-bold text-white">{t('pointsOrdinal', { points: 30 })}</span> {t('bonusPointsVice')}</li>
                  <li><span className="font-bold text-white">{t('pointsOrdinal', { points: 50 })}</span> {t('bonusPointsTopScorer')}</li>
                  {pool.knockoutMultiplierPhase && pool.knockoutMultiplierPhase !== 'Nenhuma' && (
                    <li className="mt-4 font-bold text-yellow-400">
                      A partir da fase {pool.knockoutMultiplierPhase}, todas as partidas valem o dobro de pontos. (Peso 2)
                    </li>
                  )}
                </ul>
              )}
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white mb-4">{t('limitsAndLocks')}</h3>
              <ul className="list-disc list-inside space-y-2">
                <li><span className="font-bold text-white">{t('bettingDeadline')}</span> {t('beforeMatchStart')} {championship?.type === 'Formula 1' ? t('eachRace') : t('eachMatch')}.</li>
                {championship?.type === 'Formula 1' && (
                  <li>{t('f1SeasonDeadline')}</li>
                )}
                {championship?.type !== 'Formula 1' && (
                  <>
                    <li><span className="font-bold text-white">{t('bonusMatchQuantity')}</span> {t('oneMatchPerRound')}</li>
                    <li>{t('soccerSeasonDeadline')}</li>
                  </>
                )}
              </ul>
            </div>
            {championship?.type !== 'Formula 1' && (
              <div className="mt-8">
                <h3 className="text-2xl font-bold text-white mb-4">{t('tiebreakerCriteria')}</h3>
                <ul className="list-none space-y-2">
                  <li><span className="text-white">{t('tiebreaker1')}</span></li>
                  <li><span className="text-white">{t('tiebreaker2')}</span></li>
                  <li><span className="text-white">{t('tiebreaker3')}</span></li>
                  <li><span className="text-white">{t('tiebreaker4')}</span></li>
                  <li><span className="text-white">{t('tiebreaker5')}</span></li>
                </ul>
              </div>
            )}
          </div>
        )}

        {activeTab === 'participantes' && (
          <div className="bg-gray-800/50 rounded-xl p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
              <h3 className="text-2xl font-bold text-white flex items-center"><Icon name="users" />{t('participants')}</h3>
              <div className="flex bg-gray-900/50 p-1 rounded-lg">
                <button
                  onClick={() => setPaymentFilter('todos')}
                  className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${paymentFilter === 'todos' ? 'bg-teal-500 text-white' : 'text-gray-400 hover:text-gray-200'}`}
                >
                  {t('all')}
                </button>
                <button
                  onClick={() => setPaymentFilter('pago')}
                  className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${paymentFilter === 'pago' ? 'bg-green-600 text-white' : 'text-gray-400 hover:text-gray-200'}`}
                >
                  {t('paidLabel')}
                </button>
                <button
                  onClick={() => setPaymentFilter('pendente')}
                  className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${paymentFilter === 'pendente' ? 'bg-red-600 text-white' : 'text-gray-400 hover:text-gray-200'}`}
                >
                  {t('pending')}
                </button>
              </div>
            </div>

            <ul className="space-y-3">
              {(pool.participants || [])
                .filter(p => {
                  if (paymentFilter === 'pago') return p.paid === true;
                  if (paymentFilter === 'pendente') return !p.paid;
                  return true;
                })
                .map(p => (
                  <li key={p.uid} className={`flex items-center justify-between text-gray-300 p-3 rounded-md bg-gray-700/30 border border-gray-600/50 ${p.status === 'inactive' ? 'opacity-50' : ''}`}>
                    <div className="flex flex-col">
                      <span className="font-medium">{p.displayName || t('participant')} {p.status === 'inactive' && <span className="text-xs text-red-400 ml-1">{t('inactive')}</span>}</span>
                      {p.paid ? (
                        <span className="bg-green-500/20 text-green-400 text-[10px] font-bold px-2 py-0.5 rounded-full border border-green-500/30 flex items-center w-fit gap-1 uppercase tracking-wider">
                          <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                          {t('paidLabel')}
                        </span>
                      ) : (
                        <span className="bg-red-500/10 text-red-400 text-[10px] font-bold px-2 py-0.5 rounded-full border border-red-500/20 w-fit uppercase tracking-wider">
                          {t('pendingLabel')}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {user.uid === pool.adminId && (
                        <button
                          onClick={() => handleTogglePaymentStatus(p)}
                          className={`text-xs px-3 py-1.5 rounded-lg font-bold transition-all ${p.paid ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-green-600 text-white hover:bg-green-500 shadow-lg shadow-green-900/20'}`}
                        >
                          {p.paid ? t('rejectPayment') : t('confirmPayment')}
                        </button>
                      )}
                      {user.uid === pool.adminId && user.uid !== p.uid && (
                        <button
                          onClick={() => handleToggleParticipantStatus(p)}
                          className={`text-xs px-3 py-1.5 rounded-lg font-bold transition-all ${p.status === 'inactive' ? 'bg-blue-600 hover:bg-blue-500' : 'bg-red-600 hover:bg-red-500'} text-white`}
                        >
                          {p.status === 'inactive' ? t('reactivate') : t('inactivate')}
                        </button>
                      )}
                    </div>
                  </li>
                ))}
            </ul>
            {(pool.participants || []).filter(p => {
              if (paymentFilter === 'pago') return p.paid === true;
              if (paymentFilter === 'pendente') return !p.paid;
              return true;
            }).length === 0 && (
                <p className="text-center text-gray-500 py-8 italic">{t('noParticipantsFound')}</p>
              )}
          </div>
        )}
        </ErrorBoundary>
      </div>

        {activeTab === 'f1_drivers_standings' && championship?.type === 'Formula 1' && (
          <div className="bg-gray-800/50 rounded-xl p-6">
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2"><span className="text-yellow-400">🏅</span> {t('driversClassification')}</h3>
            {!f1StandingsDrivers || !f1StandingsDrivers.standings ? (
               <p className="text-gray-400">{t('classificationUnavailable')}</p>
            ) : (
               <div className="overflow-x-auto">
                 <table className="w-full text-left text-gray-300 border-collapse min-w-[300px]">
                   <thead>
                     <tr className="bg-gray-700/50 text-sm">
                       <th className="p-3 border-b border-gray-600">{t('pos')}</th>
                       <th className="p-3 border-b border-gray-600">{t('driver')}</th>
                       <th className="p-3 border-b border-gray-600 text-center hidden sm:table-cell">{t('nationality')}</th>
                       <th className="p-3 border-b border-gray-600 text-right">{t('pointsLabel')}</th>
                     </tr>
                   </thead>
                   <tbody>
                     {f1StandingsDrivers.standings.entries?.map((entry, idx) => {
                       const pos = entry.stats?.find(s => s.name === 'rank')?.value || idx + 1;
                       const pts = entry.stats?.find(s => s.name === 'championshipPts')?.value || "-";
                       const name = entry.athlete?.displayName || entry.athlete?.shortName || "-";
                       const flag = entry.athlete?.flag?.alt || "";
                       return (
                         <tr key={idx} className="border-b border-gray-700 hover:bg-gray-700/30">
                           <td className="p-3 font-semibold">{pos}</td>
                           <td className="p-3 font-semibold text-white">
                             {name}
                           </td>
                           <td className="p-3 text-center hidden sm:table-cell text-xs">{flag}</td>
                           <td className="p-3 text-right font-bold text-teal-400">{pts}</td>
                         </tr>
                       );
                     })}
                   </tbody>
                 </table>
               </div>
            )}
          </div>
        )}

        {activeTab === 'f1_teams_standings' && championship?.type === 'Formula 1' && (
          <div className="bg-gray-800/50 rounded-xl p-6">
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2"><span className="text-yellow-400">🏎️</span> {t('teamsClassification')}</h3>
            {!f1StandingsTeams || !f1StandingsTeams.standings ? (
               <p className="text-gray-400">{t('classificationUnavailable')}</p>
            ) : (
               <div className="overflow-x-auto">
                 <table className="w-full text-left text-gray-300 border-collapse min-w-[300px]">
                   <thead>
                     <tr className="bg-gray-700/50 text-sm">
                       <th className="p-3 border-b border-gray-600">{t('pos')}</th>
                       <th className="p-3 border-b border-gray-600">{t('team')}</th>
                       <th className="p-3 border-b border-gray-600 text-right">{t('pointsLabel')}</th>
                     </tr>
                   </thead>
                   <tbody>
                     {f1StandingsTeams.standings.entries?.map((entry, idx) => {
                       const pos = entry.stats?.find(s => s.name === 'rank')?.value || idx + 1;
                       const pts = entry.stats?.find(s => s.name === 'points')?.value || "-";
                       const name = entry.team?.displayName || entry.team?.name || "-";
                       return (
                         <tr key={idx} className="border-b border-gray-700 hover:bg-gray-700/30">
                           <td className="p-3 font-semibold">{pos}</td>
                           <td className="p-3 font-semibold text-white">
                             {name}
                           </td>
                           <td className="p-3 text-right font-bold text-teal-400">{pts}</td>
                         </tr>
                       );
                     })}
                   </tbody>
                 </table>
               </div>
            )}
          </div>
        )}

      {/* --- NOVO MODAL RENDER --- */}
      {showBetsModal && (
        <BetsModal
          match={matches.find(m => m.id === showBetsModal)} // Encontra o jogo pelo ID
          pool={pool}
          allBets={bets} // Passa todos os palpites
          onClose={() => setShowBetsModal(null)}
        />
      )}
      {showBonusBetsModal && (
        <SeasonBetsModal
          pool={pool}
          allBets={bets}
          championship={championship}
          onClose={() => setShowBonusBetsModal(false)}
        />
      )}
      {/* --- FIM DO NOVO MODAL --- */}

      {/* Edit Pool Modal */}
      {showEditPoolModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-gray-800 rounded-xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-white mb-6">{t('editPool')}</h3>
            <form onSubmit={handleUpdatePool}>
              <div className="mb-4">
                <label className="block text-gray-400 mb-2">{t('poolName')}</label>
                <input
                  type="text"
                  value={editPoolName}
                  onChange={e => setEditPoolName(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-white"
                  required
                />
              </div>

              <div className="mb-4 p-4 bg-gray-700/50 rounded-lg border border-gray-600">
                <div className="flex items-center mb-4">
                  <input
                    id="editIsPublic"
                    type="checkbox"
                    checked={editPoolIsPublic}
                    onChange={e => setEditPoolIsPublic(e.target.checked)}
                    className="w-5 h-5 text-teal-500 rounded focus:ring-teal-500 border-gray-600 bg-gray-700"
                  />
                  <label htmlFor="editIsPublic" className="ml-2 text-gray-300 font-medium cursor-pointer">
                    {t('publicPool')}
                  </label>
                </div>

                <div className="mb-4">
                  <label className="block text-gray-400 mb-2 text-sm">{t('changeCoverImage')}</label>
                  {pool.photoUrl && !editPoolImage && (
                    <div className="mb-2 relative w-full h-32 rounded-lg overflow-hidden group">
                      <img src={pool.photoUrl} alt="Capa Atual" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <button type="button" onClick={handleRemovePoolImage} className="bg-red-600 text-white text-xs py-1 px-3 rounded hover:bg-red-700">{t('removeImage')}</button>
                      </div>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={e => setEditPoolImage(e.target.files[0])}
                    className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-teal-500 file:text-white hover:file:bg-teal-600 cursor-pointer"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowEditPoolModal(false)}
                  className="bg-gray-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="bg-green-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-green-500 transition-colors"
                >
                  {t('saveChanges')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}





    </div>
  );
};

const ForceUpdateModal = () => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[100] p-4">
      <div className="bg-gray-800 rounded-xl p-8 max-w-md w-full text-center border-t-4 border-red-500 shadow-2xl">
        <h2 className="text-2xl font-bold text-white mb-4">Atualização Necessária</h2>
        <p className="text-gray-300 mb-6">
          Sua versão do aplicativo está desatualizada. Por favor, atualize para a versão mais recente na Play Store para continuar usando o Bolões Top.
        </p>
        <a 
          href="https://play.google.com/store/apps/details?id=br.com.boloestop.bolao" 
          target="_blank" 
          rel="noopener noreferrer"
          className="block w-full bg-teal-500 text-white font-bold py-3 px-4 rounded-lg hover:bg-teal-400 transition-colors"
        >
          Atualizar Agora
        </a>
      </div>
    </div>
  );
};

{/* --- NOVO COMPONENTE MODAL ADICIONADO --- */ }
const SeasonBetsModal = ({ pool, allBets, championship, onClose }) => {
  const { t } = useTranslation();
  const sortedParticipants = useMemo(() => {
    const participantsWithPoints = (pool?.participants || [])
      .filter(p => p.status === 'active')
      .map(participant => {
        const sBets = allBets[participant.uid]?.seasonBets || {};
        let points = 0;
        let hasResults = false;

        if (championship?.type === 'Formula 1') {
          if (championship.championDriver || championship.championTeam) hasResults = true;
          if (championship.championDriver && sBets.championDriver === championship.championDriver) points += 25;
          if (championship.championTeam && sBets.championTeam === championship.championTeam) points += 25;
        } else {
          if (championship?.resultChampion || championship?.resultVice || championship?.resultTopScorerTeam) hasResults = true;
          if (championship?.resultChampion && sBets.champion === championship.resultChampion) points += 50;
          if (championship?.resultVice && sBets.vice === championship.resultVice) points += 30;
          const isTopScorer1 = championship?.resultTopScorerTeam && sBets.topScorerTeam === championship.resultTopScorerTeam;
          const isTopScorer2 = championship?.resultTopScorerTeam2 && sBets.topScorerTeam === championship.resultTopScorerTeam2;
          const isTopScorer3 = championship?.resultTopScorerTeam3 && sBets.topScorerTeam === championship.resultTopScorerTeam3;
          if (isTopScorer1 || isTopScorer2 || isTopScorer3) points += 50;
        }

        return { ...participant, sBets, points, hasResults };
      });

    return participantsWithPoints.sort((a, b) => {
      if (b.points !== a.points) return b.points - a.points;
      return (a.displayName || "Sem nome").localeCompare(b.displayName || "Sem nome");
    });
  }, [pool, allBets, championship]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4 animate-fade-in">
      <div className="bg-gray-800 rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white">✕</button>
        <h3 className="text-2xl font-bold text-teal-400 mb-6">{t('seasonPredictions')}</h3>
        <div className="space-y-4">
          {sortedParticipants.map(participant => {
            const { sBets, points, hasResults } = participant;
            return (
              <div key={participant.uid} className="bg-gray-700/50 p-4 rounded-lg flex flex-col sm:flex-row justify-between sm:items-center gap-4">
                <div className="flex-1 flex flex-col">
                   <p className="font-bold text-white">{participant.displayName}</p>
                   {hasResults && <p className="text-sm font-bold text-teal-400 mt-1">{points} {t('pts')}</p>}
                </div>
                <div className="flex-[2] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
                  {championship?.type === 'Formula 1' ? (
                    <>
                      <div><span className="text-gray-400">{t('championDriver')}:</span> <span className="text-teal-300">{sBets.championDriver || '-'}</span></div>
                      <div><span className="text-gray-400">{t('championTeam')}:</span> <span className="text-teal-300">{sBets.championTeam || '-'}</span></div>
                    </>
                  ) : (
                    <>
                      <div><span className="text-gray-400">{t('champion')}:</span> <span className="text-teal-300">{sBets.champion || '-'}</span></div>
                      <div><span className="text-gray-400">{t('viceChampion')}:</span> <span className="text-teal-300">{sBets.vice || '-'}</span></div>
                      <div><span className="text-gray-400">{t('topScorerTeam')}:</span> <span className="text-teal-300">{sBets.topScorerTeam || '-'}</span></div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const BetsModal = ({ match, pool, allBets, onClose }) => {
  const { t } = useTranslation();
  // Calcula pontos e ordena os participantes
  const sortedParticipants = useMemo(() => {
    const participantsWithPoints = (pool?.participants || [])
      .filter(p => p.status === 'active') // Filtra apenas ativos
      .map(participant => {
        const userBetData = allBets[participant.uid] || {};
        const bet = userBetData[match.id];
        let points = 0;

        if (match.type === 'f1_race') {
          // Lógica F1
          if (match.status === 'finished' && bet) {
            const info = getF1PointsInfo(bet, match);
            points = info.points;
          }
        } else {
          // Lógica Futebol
          if (match.resultA !== null && match.resultB !== null && bet) {
            const info = getPointsInfo(bet, match, pool);
            points = info.points;
          }
        }

        return {
          ...participant,
          bet,
          points
        };
      });

    // Ordena por pontos (decrescente) e depois por nome (crescente)
    return participantsWithPoints.sort((a, b) => {
      if (b.points !== a.points) {
        return b.points - a.points;
      }
      return (a.displayName || "Participante").localeCompare(b.displayName || "Participante");
    });
  }, [pool?.participants, allBets, match]);

  if (!match) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl p-6 max-w-lg w-full max-h-[80vh] flex flex-col shadow-lg">
        <h3 className="text-2xl font-bold text-white mb-2">{t('betsClosed')}</h3>
        <p className="text-lg text-teal-300 mb-4 font-semibold">{match.teamA} {match.teamB ? `${t('vs')} ${match.teamB}` : ''}</p>

        {/* Lista de Palpites */}
        <ul className="space-y-2 overflow-y-auto pr-2 flex-grow">
          {sortedParticipants.map(participant => {
            const bet = participant.bet;
            const points = participant.points;
            const hasPoints = points > 0 || (match.status === 'finished' && bet); // Mostra 0 se errou tudo mas jogou

            // Lógica para F1
            if (match.type === 'f1_race') {
              const p1 = bet?.p1 || '-';
              const p2 = bet?.p2 || '-';
              const p3 = bet?.p3 || '-';

              return (
                <li key={participant.uid} className="flex flex-col bg-gray-700 p-3 rounded-md">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-gray-300 font-semibold">{participant.displayName || "Participante"}</span>
                    {match.status === 'finished' && (
                      <span className="text-yellow-400 font-bold text-sm bg-gray-900 px-2 py-1 rounded">
                        {points} {t('pts')}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-white grid grid-cols-3 gap-2">
                    <div><span className="text-gray-400 text-xs">1º:</span> {p1}</div>
                    <div><span className="text-gray-400 text-xs">2º:</span> {p2}</div>
                    <div><span className="text-gray-400 text-xs">3º:</span> {p3}</div>
                  </div>
                </li>
              );
            }

            // Lógica para Futebol (Padrão)
            const betA = bet?.betA ?? '-';
            const betB = bet?.betB ?? '-';
            const isBonus = bet?.isBonus === true;

            return (
              <li key={participant.uid} className="flex justify-between items-center bg-gray-700 p-3 rounded-md">
                <span className="text-gray-300">{participant.displayName || "Participante"}</span>
                <div className="flex items-center gap-3">
                  <span className="font-bold text-white text-lg flex items-center">
                    {isBonus && <span title="Partida Bônus" className="mr-2 text-yellow-400">⭐</span>}
                    {betA} x {betB}
                  </span>
                  {(match.resultA !== null && match.resultB !== null) && (
                    <span className="text-yellow-400 font-bold text-sm bg-gray-900 px-2 py-1 rounded w-16 text-center">
                      {points} {t('pts')}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>

        {/* Botão de Fechar */}
        <div className="mt-6 text-right pt-4 border-t border-gray-700">
          <button
            onClick={onClose}
            className="bg-gray-600 text-white font-bold py-2 px-5 rounded-lg hover:bg-gray-700 transition-colors"
          >
            {t('close')}
          </button>
        </div>
      </div>
    </div>
  );
};
{/* --- FIM DO NOVO COMPONENTE --- */ }


// Hook para obter o valor anterior de um prop ou estado
const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

/**
 * Painel de Administração Geral
 */
const AdminPanel = ({ user }) => {
  const { t, i18n } = useTranslation();
  const [championships, setChampionships] = useState([]);
  const [matches, setMatches] = useState([]);
  const [allRegisteredTeams, setAllRegisteredTeams] = useState([]);
  const [selectedChampId, setSelectedChampId] = useState('');
  const [adminTab, setAdminTab] = useState('championships'); // 'championships', 'teams'

  const [newChampName, setNewChampName] = useState('');
  const [newChampType, setNewChampType] = useState('Liga');

  const [newMatchTeamA, setNewMatchTeamA] = useState('');
  const [newMatchTeamB, setNewMatchTeamB] = useState('');
  const [newMatchDate, setNewMatchDate] = useState('');
  const [newMatchPhase, setNewMatchPhase] = useState('');

  const [editingResults, setEditingResults] = useState({});
  const [editingMatchId, setEditingMatchId] = useState(null);
  const [editingMatchDate, setEditingMatchDate] = useState('');

  const [groups, setGroups] = useState([]);
  const [newGroupName, setNewGroupName] = useState('');
  const [selectedTeamToAdd, setSelectedTeamToAdd] = useState('');
  const [selectedGroupForTeam, setSelectedGroupForTeam] = useState('');

  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamLogo, setNewTeamLogo] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [phases, setPhases] = useState([]);
  const [newPhaseName, setNewPhaseName] = useState('');

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [matchToDelete, setMatchToDelete] = useState(null);
  const [showDeleteChampConfirm, setShowDeleteChampConfirm] = useState(false);
  const [champToDelete, setChampToDelete] = useState(null);

  const [adminError, setAdminError] = useState('');

  // Estados para F1
  const [newRaceCircuit, setNewRaceCircuit] = useState('');
  const [newRaceCountry, setNewRaceCountry] = useState('');
  const [seasonChampionDriver, setSeasonChampionDriver] = useState('');
  const [seasonChampionTeam, setSeasonChampionTeam] = useState('');
  const [f1Drivers, setF1Drivers] = useState([]);
  const [f1Teams, setF1Teams] = useState([]);

  // Estados para Bônus Futebol
  const [resultChampion, setResultChampion] = useState('');
  const [resultVice, setResultVice] = useState('');
  const [resultTopScorerTeam, setResultTopScorerTeam] = useState('');
  const [resultTopScorerTeam2, setResultTopScorerTeam2] = useState('');
  const [resultTopScorerTeam3, setResultTopScorerTeam3] = useState('');

  const selectedChampionship = championships.find(c => c.id === selectedChampId);
  const prevSelectedChampId = usePrevious(selectedChampId);

  // Efeito para buscar todos os times cadastrados
  useEffect(() => {
    const teamsQuery = query(collection(db, "teams"));
    const unsubscribe = onSnapshot(teamsQuery, (snapshot) => {
      const teams = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllRegisteredTeams(teams);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const q = query(collection(db, "championships"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setChampionships(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!selectedChampId) {
      setMatches([]);
      setEditingResults({});
      setGroups([]);
      setPhases([]);
      setSelectedGroupForTeam('');
      return;
    }

    const champRef = doc(db, "championships", selectedChampId);
    const unsubChamp = onSnapshot(champRef, (docSnap) => {
      const champData = docSnap.data();
      if (champData) {
        setGroups(champData.groups || []);
        setPhases(champData.phases || []);
        if (selectedChampId !== prevSelectedChampId) {
          if ((champData.groups || []).length > 0) {
            setSelectedGroupForTeam(champData.groups[0].name);
          } else {
            setSelectedGroupForTeam('');
          }
          if ((champData.phases || []).length > 0) {
            setNewMatchPhase(champData.phases[0]);
          } else {
            setNewMatchPhase('');
          }
        }
      }
    });

    const q = query(collection(db, "matches"), where("championshipId", "==", selectedChampId));
    const unsubscribeMatches = onSnapshot(q, (snapshot) => {
      const fetchedMatches = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) => {
        const aTs = a.matchDate && a.matchDate.seconds ? a.matchDate.seconds : 0;
        const bTs = b.matchDate && b.matchDate.seconds ? b.matchDate.seconds : 0;
        return aTs - bTs;
      });
      setMatches(fetchedMatches);

      const newResults = {};
      fetchedMatches.forEach(match => {
        newResults[match.id] = {
          resultA: match.resultA !== null && match.resultA !== undefined ? String(match.resultA) : '',
          resultB: match.resultB !== null && match.resultB !== undefined ? String(match.resultB) : '',
          resultP1: match.resultP1 || '',
          resultP2: match.resultP2 || '',
          resultP3: match.resultP3 || '',
          resultA_ext: match.resultA_ext !== null && match.resultA_ext !== undefined ? String(match.resultA_ext) : '',
          resultB_ext: match.resultB_ext !== null && match.resultB_ext !== undefined ? String(match.resultB_ext) : '',
          extraTimeMethod: match.extraTimeMethod || ''
        };
      });
      setEditingResults(newResults);
    });




    // Carregar resultados da temporada se for F1
    if (selectedChampionship && selectedChampionship.type === 'Formula 1') {
      setSeasonChampionDriver(selectedChampionship.championDriver || '');
      setSeasonChampionTeam(selectedChampionship.championTeam || '');

      // Carregar pilotos e equipes para o dropdown
      const fetchF1Options = async () => {
        const driversQ = query(collection(db, "f1_drivers"));
        const driversSnap = await getDocs(driversQ);
        const driversData = driversSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        driversData.sort((a, b) => a.name.localeCompare(b.name));
        setF1Drivers(driversData);

        const teamsQ = query(collection(db, "f1_teams"));
        const teamsSnap = await getDocs(teamsQ);
        const teamsData = teamsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        teamsData.sort((a, b) => a.name.localeCompare(b.name));
        setF1Teams(teamsData);
      };
      fetchF1Options();
    } else if (selectedChampionship) {
      setResultChampion(selectedChampionship.resultChampion || '');
      setResultVice(selectedChampionship.resultVice || '');
      setResultTopScorerTeam(selectedChampionship.resultTopScorerTeam || '');
      setResultTopScorerTeam2(selectedChampionship.resultTopScorerTeam2 || '');
      setResultTopScorerTeam3(selectedChampionship.resultTopScorerTeam3 || '');
    }

    return () => {
      try { unsubChamp(); } catch (e) { /* ignore */ }
      try { unsubscribeMatches(); } catch (e) { /* ignore */ }
    };
  }, [selectedChampId, prevSelectedChampId]);

  const handleAddChampionship = async (e) => {
    e.preventDefault();
    setAdminError('');
    if (!newChampName) return;
    const newChamp = {
      name: newChampName,
      type: newChampType,
      status: 'inactive'
    };
    if (newChampType === 'Copa') {
      newChamp.groups = [];
      newChamp.phases = [];
    } else {
      newChamp.teams = [];
    }
    await addDoc(collection(db, "championships"), newChamp);
    setNewChampName('');
  };

  const handleSaveTeam = async (e) => {
    e.preventDefault();
    const form = e.target;
    setAdminError('');
    if (!newTeamName || isSubmitting) return;

    const teamExists = allRegisteredTeams.some(team => team.name.toLowerCase() === newTeamName.toLowerCase());
    if (teamExists) {
      setAdminError("Um time com este nome já foi cadastrado.");
      return;
    }

    setIsSubmitting(true);
    try {
      let logoUrl = null;
      if (newTeamLogo) {
        const storageRef = ref(storage, `team-logos/${newTeamLogo.name}_${Date.now()}`);
        await uploadBytes(storageRef, newTeamLogo);
        logoUrl = await getDownloadURL(storageRef);
      }

      const newTeamObject = { name: newTeamName, logoUrl };
      await setDoc(doc(db, "teams", newTeamName), newTeamObject);

      setNewTeamName('');
      setNewTeamLogo(null);
      if (form.querySelector('input[type="file"]')) {
        form.querySelector('input[type="file"]').value = '';
      }
    } catch (error) {
      console.error("Erro ao salvar time:", error);
      setAdminError("Ocorreu um erro ao salvar o time. Verifique as permissões do Firebase Storage.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteRegisteredTeam = async (teamToDelete) => {
    if (isSubmitting) return;
    setAdminError('');
    setIsSubmitting(true);
    try {
      await deleteDoc(doc(db, "teams", teamToDelete.id));

      if (teamToDelete.logoUrl) {
        try {
          const logoRef = ref(storage, teamToDelete.logoUrl);
          await deleteObject(logoRef);
        } catch (error) {
          if (error.code !== 'storage/object-not-found') {
            console.error(t('errorDeletingLogo'), error);
          }
        }
      }
    } catch (error) {
      console.error(t('errorDeletingTeam'), error);
      setAdminError(t('errorDeletingTeamGeneric'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSetChampStatus = async (champId, newStatus) => {
    setAdminError('');
    const champRef = doc(db, "championships", champId);
    await updateDoc(champRef, {
      status: newStatus
    });
  };

  const handleAddTeamToChampionship = async (e) => {
    e.preventDefault();
    setAdminError('');
    if (!selectedTeamToAdd || !selectedChampId || isSubmitting) return;

    const teamObject = allRegisteredTeams.find(t => t.id === selectedTeamToAdd);
    if (!teamObject) return;

    setIsSubmitting(true);
    try {
      const champRef = doc(db, "championships", selectedChampId);

      if (selectedChampionship && selectedChampionship.type === 'Copa') {
        if (!selectedGroupForTeam) {
          setAdminError("Por favor, selecione um grupo.");
          setIsSubmitting(false);
          return;
        }
        const champDocSnap = await getDoc(champRef);
        const currentChampData = champDocSnap.data();
        const updatedGroups = (currentChampData.groups || []).map(group => {
          if (group.name === selectedGroupForTeam) {
            const teamExists = (group.teams || []).some(t => t.name === teamObject.name);
            if (!teamExists) {
              return { ...group, teams: [...(group.teams || []), teamObject] };
            }
          }
          return group;
        });
        await updateDoc(champRef, { groups: updatedGroups });
      } else {
        await updateDoc(champRef, { teams: arrayUnion(teamObject) });
      }

    } catch (error) {
      console.error("Erro ao adicionar time ao campeonato:", error);
      setAdminError("Ocorreu um erro ao adicionar o time ao campeonato.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTeamFromChampionship = async (teamToDelete, groupName = null) => {
    if (!selectedChampId || isSubmitting) return;
    setAdminError('');
    setIsSubmitting(true);
    try {
      const champRef = doc(db, "championships", selectedChampId);

      const champDocSnap = await getDoc(champRef);
      if (!champDocSnap.exists()) throw new Error("Campeonato não encontrado!");
      const currentChampData = champDocSnap.data();

      if (currentChampData.type === 'Copa') {
        const updatedGroups = (currentChampData.groups || []).map(group => {
          if (group.name === groupName) {
            const updatedTeams = (group.teams || []).filter(team => team.name !== teamToDelete.name);
            return { ...group, teams: updatedTeams };
          }
          return group;
        });
        await updateDoc(champRef, { groups: updatedGroups });
      } else {
        const updatedTeams = (currentChampData.teams || []).filter(team => team.name !== teamToDelete.name);
        await updateDoc(champRef, { teams: updatedTeams });
      }
    } catch (error) {
      console.error("Erro ao deletar time do campeonato:", error);
      setAdminError("Ocorreu um erro ao deletar o time do campeonato.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddGroup = async (e) => {
    e.preventDefault();
    setAdminError('');
    if (!newGroupName || !selectedChampId) return;
    const newGroup = { name: newGroupName, teams: [] };
    const champRef = doc(db, "championships", selectedChampId);
    await updateDoc(champRef, {
      groups: arrayUnion(newGroup)
    });
    setSelectedGroupForTeam(newGroupName);
    setNewGroupName('');
  };

  const handleAddMatch = async (e) => {
    e.preventDefault();
    setAdminError('');
    if (!newMatchTeamA || !newMatchTeamB || !newMatchDate || !selectedChampId) return;

    const newMatch = {
      championshipId: selectedChampId,
      teamA: newMatchTeamA,
      teamB: newMatchTeamB,
      matchDate: new Date(newMatchDate),
      resultA: null,
      resultB: null,
      status: 'scheduled'
    };
    if (selectedChampionship?.type === 'Copa' && newMatchPhase) {
      newMatch.phase = newMatchPhase;
    }

    await addDoc(collection(db, "matches"), newMatch);
    setNewMatchTeamA('');
    setNewMatchTeamB('');
    setNewMatchDate('');
  };

  const handleAddRace = async (e) => {
    e.preventDefault();
    setAdminError('');
    if (!newRaceCircuit || !newRaceCountry || !newMatchDate || !selectedChampId) return;

    const newRace = {
      championshipId: selectedChampId,
      circuit: newRaceCircuit,
      country: newRaceCountry,
      matchDate: new Date(newMatchDate),
      type: 'f1_race',
      status: 'scheduled'
    };

    await addDoc(collection(db, "matches"), newRace);
    setNewRaceCircuit('');
    setNewRaceCountry('');
    setNewMatchDate('');
  };

  const handleSaveSeasonResults = async () => {
    if (!selectedChampId) return;
    try {
      const updates = {};
      if (selectedChampionship?.type === 'Formula 1') {
        updates.championDriver = seasonChampionDriver;
        updates.championTeam = seasonChampionTeam;
      } else {
        updates.resultChampion = resultChampion;
        updates.resultVice = resultVice;
        updates.resultTopScorerTeam = resultTopScorerTeam;
        if (selectedChampionship?.type === 'Copa') {
          updates.resultTopScorerTeam2 = resultTopScorerTeam2;
          updates.resultTopScorerTeam3 = resultTopScorerTeam3;
        }
      }
      await updateDoc(doc(db, "championships", selectedChampId), updates);
      alert("Resultados salvos com sucesso!");
    } catch (error) {
      console.error("Erro ao salvar resultados:", error);
      setAdminError("Erro ao salvar resultados.");
    }
  };

  const handleUpdateResult = async (matchId) => {
    const results = editingResults[matchId];
    if (!results) return;

    const match = matches.find(m => m.id === matchId);
    if (!match) return;

    let newStatus = 'scheduled';
    const updateData = {};

    if (match.type === 'f1_race') {
      if (results.resultP1) updateData.resultP1 = results.resultP1;
      else updateData.resultP1 = null;

      if (results.resultP2) updateData.resultP2 = results.resultP2;
      else updateData.resultP2 = null;

      if (results.resultP3) updateData.resultP3 = results.resultP3;
      else updateData.resultP3 = null;

      if (updateData.resultP1 && updateData.resultP2 && updateData.resultP3) {
        newStatus = 'finished';
      }
    } else {
      if (results.resultA !== '' && results.resultA !== undefined && results.resultA !== null) {
        updateData.resultA = parseInt(results.resultA, 10);
      } else {
        updateData.resultA = null;
      }

      if (results.resultB !== '' && results.resultB !== undefined && results.resultB !== null) {
        updateData.resultB = parseInt(results.resultB, 10);
      } else {
        updateData.resultB = null;
      }

      if (results.resultA_ext !== '' && results.resultA_ext !== undefined && results.resultA_ext !== null) {
        updateData.resultA_ext = parseInt(results.resultA_ext, 10);
      } else {
        updateData.resultA_ext = null;
      }

      if (results.resultB_ext !== '' && results.resultB_ext !== undefined && results.resultB_ext !== null) {
        updateData.resultB_ext = parseInt(results.resultB_ext, 10);
      } else {
        updateData.resultB_ext = null;
      }

      if (results.extraTimeMethod) updateData.extraTimeMethod = results.extraTimeMethod;
      else updateData.extraTimeMethod = null;

      const hasScoreA = updateData.resultA !== null && !isNaN(updateData.resultA);
      const hasScoreB = updateData.resultB !== null && !isNaN(updateData.resultB);

      if (hasScoreA && hasScoreB) {
        newStatus = 'finished';
      }
    }

    updateData.status = newStatus;

    await updateDoc(doc(db, "matches", matchId), updateData);
    setEditingMatchId(null);
  };

  const handleAddPhase = async (e) => {
    e.preventDefault();
    setAdminError('');
    if (!newPhaseName || !selectedChampId) return;
    const champRef = doc(db, "championships", selectedChampId);
    await updateDoc(champRef, {
      phases: arrayUnion(newPhaseName)
    });
    setNewPhaseName('');
  };

  const handleDeletePhase = async (phaseToDelete) => {
    setAdminError('');
    if (!selectedChampId) return;
    const champRef = doc(db, "championships", selectedChampId);
    await updateDoc(champRef, {
      phases: arrayRemove(phaseToDelete)
    });
  };

  const formatDateForInput = (date) => {
    const d = new Date(date);
    return d.toISOString().slice(0, 16);
  };

  const handleEditDateClick = (matchId, currentDate) => {
    setEditingMatchId(matchId);
    setEditingMatchDate(formatDateForInput(currentDate));
  };

  const handleCancelEdit = () => {
    setEditingMatchId(null);
    setEditingMatchDate('');
  };

  const handleSaveDate = async (matchId) => {
    if (!editingMatchDate) return;
    const matchRef = doc(db, "matches", matchId);
    await updateDoc(matchRef, { matchDate: new Date(editingMatchDate) });
    handleCancelEdit();
  };

  const handleDeleteMatchClick = (matchId) => {
    setMatchToDelete(matchId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteMatch = async () => {
    if (!matchToDelete) return;
    try {
      await deleteDoc(doc(db, "matches", matchToDelete));
    } catch (error) {
      console.error("Erro ao excluir o jogo:", error);
    } finally {
      setShowDeleteConfirm(false);
      setMatchToDelete(null);
    }
  };

  const handleDeleteChampionshipClick = (champId) => {
    setChampToDelete(champId);
    setShowDeleteChampConfirm(true);
  };

  const confirmDeleteChampionship = async () => {
    if (!champToDelete) return;
    setIsSubmitting(true);
    try {
      const matchesQuery = query(collection(db, "matches"), where("championshipId", "==", champToDelete));
      const matchesSnapshot = await getDocs(matchesQuery);
      const deletePromises = [];
      matchesSnapshot.forEach((matchDoc) => {
        deletePromises.push(deleteDoc(doc(db, "matches", matchDoc.id)));
      });
      await Promise.all(deletePromises);
      await deleteDoc(doc(db, "championships", champToDelete));
    } catch (error) {
      console.error("Erro ao excluir o campeonato:", error);
      setAdminError("Ocorreu um erro ao excluir o campeonato.");
    } finally {
      setShowDeleteChampConfirm(false);
      setChampToDelete(null);
      if (selectedChampId === champToDelete) {
        setSelectedChampId('');
      }
      setIsSubmitting(false);
    }
  };

  const handleResultChange = (matchId, field, value) => {
    setEditingResults(prev => ({ ...prev, [matchId]: { ...prev[matchId], [field]: value } }));
  };

  const handleResultBlur = (matchId) => {
    handleUpdateResult(matchId);
  };

  const AdminTabButton = ({ tabName, label }) => (
    <button
      onClick={() => setAdminTab(tabName)}
      className={`px-4 py-2 rounded-md font-semibold ${adminTab === tabName ? 'bg-teal-500 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
    >
      {label}
    </button>
  );

  if (user.uid !== ADMIN_UID) {
    return <div className="p-8 text-white">Acesso Negado.</div>;
  }

  return (
    <div className="p-8 text-white">
      <h2 className="text-3xl font-bold mb-6">Painel do Administrador</h2>
      {adminError && <p className="bg-red-500/20 text-red-300 p-3 rounded-lg mb-4">{adminError}</p>}

      <div className="flex space-x-4 mb-6 border-b border-gray-700 pb-4">
        <AdminTabButton tabName="championships" label="Gerenciar Campeonatos e Jogos" />
        <AdminTabButton tabName="teams" label="Cadastrar Times" />
        <AdminTabButton tabName="f1_teams" label="Equipes F1" />
        <AdminTabButton tabName="f1_drivers" label="Pilotos F1" />
        <AdminTabButton tabName="f1_standings" label="Classificação F1 API" />
      </div>

      {adminTab === 'teams' && (
        <div className="bg-gray-800 p-6 rounded-lg mb-8">
          <h3 className="text-xl font-bold mb-4">Cadastrar Novo Time</h3>
          <form onSubmit={handleSaveTeam} className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 items-center">
            <input type="text" value={newTeamName} onChange={e => setNewTeamName(e.target.value)} placeholder="Nome do Time" className="bg-gray-700 p-2 rounded-md" />
            <input type="file" onChange={e => setNewTeamLogo(e.target.files[0])} className="text-sm" />
            <button type="submit" disabled={isSubmitting} className="bg-green-500 p-2 rounded-md disabled:bg-gray-500">{isSubmitting ? 'Salvando...' : 'Salvar Time'}</button>
          </form>

          <div className="mt-6 border-t border-gray-700 pt-4">
            <h4 className="text-lg font-bold mb-4">Times Cadastrados</h4>
            <div className="flex flex-wrap gap-4">
              {allRegisteredTeams.map((team) => (
                <div key={team.id} className="bg-gray-700 p-2 rounded-md flex items-center gap-2">
                  {team.logoUrl && <img src={team.logoUrl} alt={team.name} className="w-6 h-6 rounded-full object-cover" />}
                  <span className="text-sm">{team.name}</span>
                  <button onClick={() => handleDeleteRegisteredTeam(team)} disabled={isSubmitting} className="text-red-500 hover:text-red-400 font-bold text-lg disabled:text-gray-500 ml-2">Excluir</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {adminTab === 'f1_teams' && <F1TeamsManager />}
      {adminTab === 'f1_drivers' && <F1DriversManager />}
      {adminTab === 'f1_standings' && <F1StandingsAdmin />}

      {adminTab === 'championships' && (
        <>
          <div className="bg-gray-800 p-6 rounded-lg mb-8">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Gerenciar Campeonatos</h3>
              <button
                onClick={() => runDailyReminder()}
                className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg flex items-center gap-2 transition-colors text-sm"
              >
                <Icon name="spinner" /> Testar Notificação Diária (Cron)
              </button>


            </div>
            <form onSubmit={handleAddChampionship} className="flex flex-wrap gap-4 items-end mb-6">
              <div className="flex-grow">
                <label className="block text-sm mb-1">Nome do Campeonato</label>
                <input type="text" value={newChampName} onChange={e => setNewChampName(e.target.value)} placeholder="Ex: Copa do Mundo 2026" className="w-full bg-gray-700 p-2 rounded-md" />
              </div>
              <div>
                <label className="block text-sm mb-1">Tipo</label>
                <select value={newChampType} onChange={e => setNewChampType(e.target.value)} className="w-full bg-gray-700 p-2 rounded-md">
                  <option value="Liga">Liga</option>
                  <option value="Copa">Copa/Torneio</option>
                  <option value="Formula 1">Formula 1</option>
                </select>
              </div>
              <button type="submit" className="bg-teal-500 p-2 rounded-md self-end h-10">Adicionar</button>
            </form>
            <div className="mt-6 border-t border-gray-700 pt-4">
              <h4 className="text-lg font-bold mb-4">Campeonatos Cadastrados</h4>
              <div className="space-y-3">
                {championships.map(champ => (
                  <div key={champ.id} className="bg-gray-700 p-3 rounded-lg flex justify-between items-center">
                    <div>
                      <span className="font-semibold">{t(champ.name)}</span>
                      <span className="text-sm text-gray-400 ml-2">({champ.type})</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {champ.status === 'inactive' && (
                        <button onClick={() => handleSetChampStatus(champ.id, 'active')} className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded-md text-sm font-bold">
                          Ativar
                        </button>
                      )}
                      {champ.status === 'active' && (
                        <>
                          <button onClick={() => handleSetChampStatus(champ.id, 'inactive')} className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-md text-sm font-bold">
                            Inativar
                          </button>
                          <button onClick={() => handleSetChampStatus(champ.id, 'finished')} className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded-md text-sm font-bold">
                            Encerrar
                          </button>
                        </>
                      )}
                      {champ.status === 'finished' && (
                        <button onClick={() => handleSetChampStatus(champ.id, 'inactive')} className="bg-yellow-500 hover:bg-yellow-600 text-black px-3 py-1 rounded-md text-sm font-bold">
                          Reativar
                        </button>
                      )}
                      <button onClick={() => handleDeleteChampionshipClick(champ.id)} className="bg-gray-600 hover:bg-red-700 text-white px-3 py-1 rounded-md text-sm font-bold">
                        Excluir
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-gray-800 p-6 rounded-lg">
            <h3 className="text-xl font-bold mb-4">Gerenciar Grupos e Jogos</h3>
            <label className="block mb-2">Selecione um Campeonato</label>
            <select value={selectedChampId} onChange={e => setSelectedChampId(e.target.value)} className="bg-gray-700 p-2 rounded-md w-full mb-6">
              <option value="">-- Selecione --</option>
              {championships.map(c => <option key={c.id} value={c.id}>{t(c.name)}</option>)}
            </select>

            {selectedChampionship?.type === 'Liga' && (
              <div className="bg-gray-700/50 p-4 rounded-lg mb-6">
                <h4 className="text-lg font-bold mb-4">Adicionar Time à Liga</h4>
                <form onSubmit={handleAddTeamToChampionship} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 items-center">
                  <select value={selectedTeamToAdd} onChange={e => setSelectedTeamToAdd(e.target.value)} className="bg-gray-700 p-2 rounded-md">
                    <option value="">Selecione um Time</option>
                    {allRegisteredTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                  <button type="submit" disabled={isSubmitting} className="bg-blue-500 p-2 rounded-md disabled:bg-gray-500">{isSubmitting ? 'Adicionando...' : 'Adicionar Time'}</button>
                </form>
                <div className="flex flex-wrap gap-4">
                  {(selectedChampionship.teams || []).map((team) => (
                    <div key={team.name} className="bg-gray-800 p-2 rounded-md flex items-center gap-2">
                      {team.logoUrl && <img src={team.logoUrl} alt={team.name} className="w-6 h-6 rounded-full object-cover" />}
                      <span className="text-sm">{team.name}</span>
                      <button onClick={() => handleDeleteTeamFromChampionship(team)} className="text-red-500 hover:text-red-400 font-bold text-lg ml-2">Excluir</button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedChampionship?.type === 'Copa' && (
              <>
                <div className="bg-gray-700/50 p-4 rounded-lg mb-6">
                  <h4 className="text-lg font-bold mb-4">Gerenciar Grupos</h4>
                  <form onSubmit={handleAddGroup} className="mb-4">
                    <label className="block text-sm mb-1">Novo Grupo</label>
                    <div className="flex gap-2">
                      <input type="text" value={newGroupName} onChange={e => setNewGroupName(e.target.value)} placeholder="Ex: Grupo A" className="flex-grow bg-gray-700 p-2 rounded-md" />
                      <button type="submit" className="bg-blue-500 p-2 rounded-md">Adicionar Grupo</button>
                    </div>
                  </form>
                  <form onSubmit={handleAddTeamToChampionship} className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 items-center">
                    <select value={selectedGroupForTeam} onChange={e => setSelectedGroupForTeam(e.target.value)} className="bg-gray-700 p-2 rounded-md">
                      <option value="">Selecione um Grupo</option>
                      {groups.map(g => <option key={g.name} value={g.name}>{g.name}</option>)}
                    </select>
                    <select value={selectedTeamToAdd} onChange={e => setSelectedTeamToAdd(e.target.value)} className="bg-gray-700 p-2 rounded-md">
                      <option value="">Selecione um Time</option>
                      {allRegisteredTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                    <button type="submit" disabled={isSubmitting} className="bg-blue-500 p-2 rounded-md disabled:bg-gray-500">{isSubmitting ? 'Adicionando...' : 'Adicionar Time'}</button>
                  </form>
                  <div className="mt-4 flex flex-wrap gap-4">
                    {groups.map(g => (
                      <div key={g.name} className="bg-gray-800 p-3 rounded-md min-w-[150px]">
                        <h5 className="font-bold border-b border-gray-700 pb-1 mb-2">{g.name}</h5>
                        <ul className="text-sm space-y-1">
                          {(g.teams || []).map(t => (
                            <li key={t.name} className="flex items-center justify-between gap-2">
                              {t.logoUrl && <img src={t.logoUrl} alt={t.name} className="w-5 h-5 rounded-full object-cover" />}
                              <span className="flex-grow">{t.name}</span>
                              <button onClick={() => handleDeleteTeamFromChampionship(t, g.name)} className="text-red-500 hover:text-red-400 font-bold text-lg">Excluir</button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gray-700/50 p-4 rounded-lg mb-6">
                  <h4 className="text-lg font-bold mb-4">Gerenciar Fases/Rodadas</h4>
                  <form onSubmit={handleAddPhase} className="mb-4">
                    <label className="block text-sm mb-1">Nova Fase/Rodada</label>
                    <div className="flex gap-2">
                      <input type="text" value={newPhaseName} onChange={e => setNewPhaseName(e.target.value)} placeholder="Ex: Oitavas de Final" className="flex-grow bg-gray-700 p-2 rounded-md" />
                      <button type="submit" className="bg-blue-500 p-2 rounded-md">Adicionar</button>
                    </div>
                  </form>
                  <div className="flex flex-wrap gap-2">
                    {phases.sort().map(phase => (
                      <div key={phase} className="bg-gray-800 p-2 rounded-md flex items-center gap-2">
                        <span className="text-sm">{phase}</span>
                        <button onClick={() => handleDeletePhase(phase)} className="text-red-500 hover:text-red-400 font-bold text-lg">&times;</button>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {selectedChampionship?.type === 'Formula 1' && (
              <div>
                <div className="bg-gray-700/50 p-4 rounded-lg mb-6">
                  <h4 className="text-lg font-bold mb-4">{t('seasonResults')}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                    <div>
                      <label className="block text-sm mb-1">{t('championDriver')}</label>
                      <select value={seasonChampionDriver} onChange={e => setSeasonChampionDriver(e.target.value)} className="w-full bg-gray-700 p-2 rounded-md">
                        <option value="">{t('select')}</option>
                        {f1Drivers.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm mb-1">{t('championTeam')}</label>
                      <select value={seasonChampionTeam} onChange={e => setSeasonChampionTeam(e.target.value)} className="w-full bg-gray-700 p-2 rounded-md">
                        <option value="">{t('select')}</option>
                        {f1Teams.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                      </select>
                    </div>
                    <button onClick={handleSaveSeasonResults} className="bg-green-600 hover:bg-green-700 text-white p-2 rounded-md h-10">{t('saveSeasonResults')}</button>
                  </div>
                </div>

                <h4 className="text-lg font-bold mb-4">Adicionar Corrida</h4>
                <form onSubmit={handleAddRace} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 items-end">
                  <input type="text" value={newRaceCircuit} onChange={e => setNewRaceCircuit(e.target.value)} placeholder="Circuito (Ex: Interlagos)" className="bg-gray-700 p-2 rounded-md" />
                  <input type="text" value={newRaceCountry} onChange={e => setNewRaceCountry(e.target.value)} placeholder="Nome da Corrida (Ex: GP do Brasil)" className="bg-gray-700 p-2 rounded-md" />
                  <input type="datetime-local" value={newMatchDate} onChange={e => setNewMatchDate(e.target.value)} className="bg-gray-700 p-2 rounded-md" />
                  <button type="submit" className="bg-teal-500 p-2 rounded-md h-10">Adicionar Corrida</button>
                </form>

                <h4 className="text-lg font-bold mb-4">Calendário de Corridas</h4>
                <div className="space-y-4">
                  {matches.map(match => (
                    <div key={match.id} className="bg-gray-700 p-4 rounded-lg flex flex-col gap-4">
                      <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex-grow min-w-[300px]">
                          <span className="font-bold block text-xl mb-1">{match.country}</span>
                          <span className="text-gray-300 block mb-1">Circuito: {match.circuit}</span>
                          {editingMatchId === match.id ? (
                            <div className="flex flex-col gap-2 mt-2 w-full">
                              <div className="flex gap-2">
                                <input type="datetime-local" value={editingMatchDate} onChange={(e) => setEditingMatchDate(e.target.value)} className="bg-gray-800 p-1 rounded-md text-sm" />
                              </div>
                              <div className="grid grid-cols-3 gap-2">
                                <select
                                  value={editingResults[match.id]?.resultP1 || ''}
                                  onChange={(e) => setEditingResults(prev => ({ ...prev, [match.id]: { ...prev[match.id], resultP1: e.target.value } }))}
                                  className="bg-gray-800 p-1 rounded-md text-sm w-full"
                                >
                                  <option value="">1º Lugar...</option>
                                  {f1Drivers.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
                                </select>
                                <select
                                  value={editingResults[match.id]?.resultP2 || ''}
                                  onChange={(e) => setEditingResults(prev => ({ ...prev, [match.id]: { ...prev[match.id], resultP2: e.target.value } }))}
                                  className="bg-gray-800 p-1 rounded-md text-sm w-full"
                                >
                                  <option value="">2º Lugar...</option>
                                  {f1Drivers.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
                                </select>
                                <select
                                  value={editingResults[match.id]?.resultP3 || ''}
                                  onChange={(e) => setEditingResults(prev => ({ ...prev, [match.id]: { ...prev[match.id], resultP3: e.target.value } }))}
                                  className="bg-gray-800 p-1 rounded-md text-sm w-full"
                                >
                                  <option value="">3º Lugar...</option>
                                  {f1Drivers.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
                                </select>
                              </div>
                              <div className="flex gap-2">
                                <button onClick={() => handleSaveDate(match.id)} className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-md text-sm transition-colors">Salvar Data</button>
                                <button onClick={() => handleUpdateResult(match.id)} className="bg-teal-600 hover:bg-teal-700 text-white px-3 py-1 rounded-md text-sm transition-colors">Salvar Resultado</button>
                                <button onClick={handleCancelEdit} className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded-md text-sm transition-colors">Cancelar</button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex flex-col gap-1 mt-1">
                              <span className="text-sm text-gray-400">{new Date((match.matchDate && match.matchDate.seconds ? match.matchDate.seconds : 0) * 1000).toLocaleString(i18n.language, { dateStyle: 'short', timeStyle: 'short' })}</span>
                              {match.status === 'finished' && (
                                <div className="text-sm text-teal-400 font-bold">
                                  Finalizado - 1º: {match.resultP1} | 2º: {match.resultP2} | 3º: {match.resultP3}
                                </div>
                              )}
                              {match.status !== 'finished' && (
                                <div className="text-sm text-yellow-400 font-bold">
                                  Agendado
                                </div>
                              )}
                              <div className="flex gap-2 mt-1">
                                <button onClick={() => handleEditDateClick(match.id, new Date((match.matchDate && match.matchDate.seconds ? match.matchDate.seconds : 0) * 1000))} className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md text-sm transition-colors">Editar</button>
                                <button onClick={() => handleDeleteMatchClick(match.id)} className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-md text-sm transition-colors">Excluir</button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedChampId && selectedChampionship?.type !== 'Formula 1' && (
              <div>
                <div className="bg-gray-700/50 p-4 rounded-lg mb-6">
                  <h4 className="text-lg font-bold mb-4">{t('seasonResults')}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                    {(() => {
                      const teams = [];
                      if (selectedChampionship?.type === 'Liga') {
                        (selectedChampionship.teams || []).forEach(t => teams.push(t));
                      } else if (selectedChampionship?.type === 'Copa') {
                        (selectedChampionship.groups || []).forEach(g => {
                          (g.teams || []).forEach(t => teams.push(t));
                        });
                      }
                      const sortedTeams = teams.sort((a, b) => a.name.localeCompare(b.name));

                      return (
                        <>
                          <div>
                            <label className="block text-sm mb-1">{t('champion')}</label>
                            <select value={resultChampion} onChange={e => setResultChampion(e.target.value)} className="w-full bg-gray-700 p-2 rounded-md">
                              <option value="">{t('select')}</option>
                              {sortedTeams.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm mb-1">{t('viceChampion')}</label>
                            <select value={resultVice} onChange={e => setResultVice(e.target.value)} className="w-full bg-gray-700 p-2 rounded-md">
                              <option value="">{t('select')}</option>
                              {sortedTeams.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm mb-1">{t('topScorerTeam')}</label>
                            <select value={resultTopScorerTeam} onChange={e => setResultTopScorerTeam(e.target.value)} className="w-full bg-gray-700 p-2 rounded-md">
                              <option value="">{t('select')}</option>
                              {sortedTeams.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
                            </select>
                          </div>
                          {selectedChampionship?.type === 'Copa' && (
                            <>
                              <div>
                                <label className="block text-sm mb-1">{t('topScorerTeam')} 2</label>
                                <select value={resultTopScorerTeam2} onChange={e => setResultTopScorerTeam2(e.target.value)} className="w-full bg-gray-700 p-2 rounded-md">
                                  <option value="">{t('select')}</option>
                                  {sortedTeams.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm mb-1">{t('topScorerTeam')} 3</label>
                                <select value={resultTopScorerTeam3} onChange={e => setResultTopScorerTeam3(e.target.value)} className="w-full bg-gray-700 p-2 rounded-md">
                                  <option value="">{t('select')}</option>
                                  {sortedTeams.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
                                </select>
                              </div>
                            </>
                          )}
                        </>
                      );
                    })()}
                    <button onClick={handleSaveSeasonResults} className="bg-green-600 hover:bg-green-700 text-white p-2 rounded-md h-10 col-span-full">{t('saveSeasonResults')}</button>
                  </div>
                </div>

                <h4 className="text-lg font-bold mb-4">Adicionar Jogo</h4>
                <form onSubmit={handleAddMatch} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6 items-end">
                  <AutocompleteInput value={newMatchTeamA} onChange={setNewMatchTeamA} placeholder="Time A" suggestions={allRegisteredTeams.map(t => t.name)} />
                  <AutocompleteInput value={newMatchTeamB} onChange={setNewMatchTeamB} placeholder="Time B" suggestions={allRegisteredTeams.map(t => t.name)} />
                  {selectedChampionship?.type === 'Copa' ? (
                    <select value={newMatchPhase} onChange={e => setNewMatchPhase(e.target.value)} className="bg-gray-700 p-2 rounded-md h-10">
                      <option value="">-- Selecione a Fase --</option>
                      {phases.sort().map(phase => (
                        <option key={phase} value={phase}>{phase}</option>
                      ))}
                    </select>
                  ) : (<div />)}
                  <input type="datetime-local" value={newMatchDate} onChange={e => setNewMatchDate(e.target.value)} className="bg-gray-700 p-2 rounded-md col-span-1 lg:col-span-2" />
                  <button type="submit" className="bg-teal-500 p-2 rounded-md h-10 col-span-full md:col-span-2 lg:col-span-1">Adicionar Jogo</button>
                </form>

                <h4 className="text-lg font-bold mb-4">Lista de Jogos</h4>
                <div className="space-y-4">
                  {matches.map(match => (
                    <div key={match.id} className="bg-gray-700 p-4 rounded-lg flex flex-col gap-4">
                      <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex-grow min-w-[300px]">
                          <span className="font-bold block">{match.teamA} vs {match.teamB}</span>
                          {match.phase && <span className="text-xs text-teal-300 bg-teal-900/50 px-2 py-1 rounded-full">{match.phase}</span>}
                          {editingMatchId === match.id ? (
                            <div className="flex items-center gap-2 mt-2">
                              <input type="datetime-local" value={editingMatchDate} onChange={(e) => setEditingMatchDate(e.target.value)} className="bg-gray-800 p-1 rounded-md" />
                              <button onClick={() => handleSaveDate(match.id)} className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-md text-sm transition-colors">Salvar</button>
                              <button onClick={handleCancelEdit} className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded-md text-sm transition-colors">Cancelar</button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-sm text-gray-400">{new Date((match.matchDate && match.matchDate.seconds ? match.matchDate.seconds : 0) * 1000).toLocaleString(i18n.language, { dateStyle: 'short', timeStyle: 'short' })}</span>
                              {match.status === 'finished' ? (
                                <span className="text-xs font-bold text-teal-400 bg-teal-500/10 px-2 py-1 rounded-full ml-2">Finalizado</span>
                              ) : (
                                <span className="text-xs font-bold text-yellow-400 bg-yellow-500/10 px-2 py-1 rounded-full ml-2">Agendado</span>
                              )}
                              <button onClick={() => handleEditDateClick(match.id, new Date((match.matchDate && match.matchDate.seconds ? match.matchDate.seconds : 0) * 1000))} className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md text-sm transition-colors">Editar / Resultado</button>
                              <button onClick={() => handleDeleteMatchClick(match.id)} className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-md text-sm transition-colors">Excluir</button>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <input type="number" placeholder="A" value={editingResults[match.id]?.resultA ?? ''} onChange={(e) => handleResultChange(match.id, 'resultA', e.target.value)} className="w-16 bg-gray-800 p-1 rounded-md text-center" />
                          <span className="text-xl">x</span>
                          <input type="number" placeholder="B" value={editingResults[match.id]?.resultB ?? ''} onChange={(e) => handleResultChange(match.id, 'resultB', e.target.value)} className="w-16 bg-gray-800 p-1 rounded-md text-center" />
                          <button onClick={() => handleUpdateResult(match.id)} className="ml-2 bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-md text-sm transition-colors">
                            Salvar
                          </button>
                        </div>
                      </div>
                      {selectedChampionship?.type === 'Copa' && (
                        <div className="flex items-center gap-2 border-t border-gray-600 pt-3">
                          <span className="text-sm text-gray-400">Prorrogação/Pênaltis:</span>
                          <input type="text" placeholder="Método (Ex: Pênaltis)" value={editingResults[match.id]?.extraTimeMethod ?? ''} onChange={e => handleResultChange(match.id, 'extraTimeMethod', e.target.value)} className="bg-gray-800 p-1 rounded-md" />
                          <input type="number" placeholder="A" value={editingResults[match.id]?.resultA_ext ?? ''} onChange={e => handleResultChange(match.id, 'resultA_ext', e.target.value)} className="w-16 bg-gray-800 p-1 rounded-md" />
                          <span className="text-xl">x</span>
                          <input type="number" placeholder="B" value={editingResults[match.id]?.resultB_ext ?? ''} onChange={e => handleResultChange(match.id, 'resultB_ext', e.target.value)} className="w-16 bg-gray-800 p-1 rounded-md" />
                          <button onClick={() => handleUpdateResult(match.id)} className="ml-2 bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-md text-sm transition-colors">
                            Salvar
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl p-8 max-w-md w-full">
            <h3 className="text-2xl font-bold text-white mb-4">Confirmar Exclusão</h3>
            <p className="text-gray-300 mb-6">Tem certeza de que deseja excluir este jogo? Esta ação não pode ser desfeita.</p>
            <div className="flex justify-end gap-4">
              <button onClick={() => setShowDeleteConfirm(false)} className="bg-gray-600 text-white font-bold py-2 px-5 rounded-lg hover:bg-gray-700 transition-colors">
                Cancelar
              </button>
              <button onClick={confirmDeleteMatch} className="bg-red-600 text-white font-bold py-2 px-5 rounded-lg hover:bg-red-700 transition-colors">
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
      {showDeleteChampConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl p-8 max-w-md w-full">
            <h3 className="text-2xl font-bold text-white mb-4">Confirmar Exclusão</h3>
            <p className="text-gray-300 mb-6">Tem certeza de que deseja excluir este campeonato? Todos os jogos associados também serão removidos. Esta ação não pode ser desfeita.</p>
            <div className="flex justify-end gap-4">
              <button onClick={() => setShowDeleteChampConfirm(false)} className="bg-gray-600 text-white font-bold py-2 px-5 rounded-lg hover:bg-gray-700 transition-colors">
                Cancelar
              </button>
              <button onClick={confirmDeleteChampionship} className="bg-red-600 text-white font-bold py-2 px-5 rounded-lg hover:bg-red-700 transition-colors">
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}



    </div>
  );
};

/**
 * Componente Principal da Aplicação
 */
function App() {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('dashboard'); // 'auth', 'dashboard', 'pool', 'admin', 'privacy_policy', 'data_deletion'
  const [poolId, setPoolId] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [updateRequired, setUpdateRequired] = useState(false);
  const [showBonusWarning, setShowBonusWarning] = useState(false);

  useEffect(() => {
    syncTime();
    
    const checkVersion = async () => {
      // Se for a versão web (navegador), não pede atualização
      if (!Capacitor.isNativePlatform()) {
        return;
      }
      try {
        const versionDoc = await getDoc(doc(db, "settings", "app_version"));
        if (versionDoc.exists()) {
          const minVersion = versionDoc.data().min_required_version;
          if (minVersion) {
            // Busca a versão real do aplicativo instalada no dispositivo
            const appInfo = await CapApp.getInfo();
            const currentVersion = appInfo.version;

            const v1 = currentVersion.split('.').map(Number);
            const v2 = minVersion.split('.').map(Number);
            for (let i = 0; i < Math.max(v1.length, v2.length); i++) {
              const num1 = v1[i] || 0;
              const num2 = v2[i] || 0;
              if (num1 < num2) {
                setUpdateRequired(true);
                return;
              } else if (num1 > num2) {
                break;
              }
            }
          }
        }
      } catch (e) {
        console.error("Erro ao checar versão:", e);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncTime(true);
        checkVersion();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    checkVersion();

    let appStateListenerPromise;
    if (Capacitor.isNativePlatform()) {
      appStateListenerPromise = CapApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          checkVersion();
        }
      });
    }

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (appStateListenerPromise) {
        appStateListenerPromise.then(listener => {
          if (listener) listener.remove();
        }).catch(() => {});
      }
    };
  }, []);

  // NOVO: Define o título da aba do navegador
  useEffect(() => {
    const getPageTitle = () => {
      switch (view) {
        case 'privacy_policy': return "Política de Privacidade - Bolões Top";
        case 'data_deletion': return "Exclusão de Dados - Bolões Top";
        case 'terms_of_service': return "Termos de Serviço - Bolões Top";
        case 'bolao_copa_2026': return "Como fazer bolão da Copa 2026 (guia completo + grátis)";
        case 'regras_bolao_online': return "Regras de bolão de futebol: guia completo para organizar sem confusão";
        case 'organizar_bolao_amigos': return "Como organizar um bolão entre amigos (sem dor de cabeça)";
        case 'pool': return "Meu Bolão - Bolões Top";
        case 'admin': return "Painel Admin - Bolões Top";
        default: return "Bolão de Futebol e Fórmula 1 - Crie o seu Grátis | Bolões Top";
      }
    };
    document.title = getPageTitle();
    if (window.location.pathname === '/politica-privacidade') {
      setView('privacy_policy');
    } else if (window.location.pathname === '/exclusao-dados') {
      setView('data_deletion');
    } else if (window.location.pathname === '/termos-servico') {
      setView('terms_of_service');
    } else if (window.location.pathname === '/bolao-copa-2026') {
      setView('bolao_copa_2026');
    } else if (window.location.pathname === '/regras-bolao-online-gratis') {
      setView('regras_bolao_online');
    } else if (window.location.pathname === '/criar-organizar-bolao-gratis') {
      setView('organizar_bolao_amigos');
    }
  }, []); // Array vazio garante que rode apenas uma vez

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });

    const urlParams = new URLSearchParams(window.location.search);
    const joinPoolId = urlParams.get('join');
    if (joinPoolId && user) {
      // Lógica para entrar no bolão ao carregar
      const joinPool = async () => {
        try {
          const poolRef = doc(db, "pools", joinPoolId);
          const poolSnap = await getDoc(poolRef);
          if (poolSnap.exists()) {
            if (!poolSnap.data().participantIds || !poolSnap.data().participantIds.includes(user.uid)) {
              await updateDoc(poolRef, {
                participants: arrayUnion({ uid: user.uid, displayName: user.displayName || (user.email ? user.email.split('@')[0] : 'Sem nome'), status: 'active' }),
                participantIds: arrayUnion(user.uid)
              });
            }
            setPoolId(joinPoolId);
            setView('pool');
            // Limpar a URL
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        } catch (error) {
          console.error("Erro ao tentar entrar no bolão via link: ", error);
        }
      };
      joinPool();
    }

    // Capacitor Native Initialization
    const initNativeHelpers = async () => {
      try {
        await StatusBar.setStyle({ style: Style.Dark });
        await StatusBar.setBackgroundColor({ color: '#0f172a' }); // Tailwind gray-900
      } catch (e) {
        console.log("StatusBar plugin is only available on native platforms.");
      }

      try {
        await SplashScreen.hide();
      } catch (e) {
        console.log("SplashScreen plugin not available.");
      }

      try {
        // App Links Listener
        const listener = await CapApp.addListener('appUrlOpen', data => {
          console.log('App opened with URL:', data);
          if (data.url.includes('join=')) {
            const urlObj = new URL(data.url);
            const nativeJoinId = urlObj.searchParams.get('join');
            if (nativeJoinId) {
              window.location.search = `?join=${nativeJoinId}`;
            }
          }
        });
        return listener;
      } catch (e) {
        console.log("App Link listener not available.");
        return null;
      }
    };
    const appUrlListenerPromise = initNativeHelpers();

    return () => {
      unsubscribe();
      appUrlListenerPromise.then(listener => {
        if (listener) {
          try {
            listener.remove();
          } catch (e) {
            console.error("Error removing appUrlOpen listener:", e);
          }
        }
      }).catch(() => {});
    };
  }, [user]);

  // Request Push Notification Permissions and Register
  useEffect(() => {
    if (user) {
      const initPush = async () => {
        try {
          const permStatus = await PushNotifications.checkPermissions();
          
          if (permStatus.receive === 'prompt') {
            const requested = await PushNotifications.requestPermissions();
            if (requested.receive !== 'granted') return;
          } else if (permStatus.receive !== 'granted') {
            return;
          }

          PushNotifications.register();
          
          PushNotifications.addListener('registration', async (token) => {
            console.log('Push registration success, token: ' + token.value);
            // Salvar no firestore de forma segura
            if (user && user.uid) {
               await setDoc(doc(db, "users", user.uid), { fcmToken: token.value }, { merge: true });
            }
          });

          PushNotifications.addListener('registrationError', (error) => {
             console.error('Error on registration: ', error);
          });
          
          PushNotifications.addListener('pushNotificationReceived', (notification) => {
             console.log('Push received: ', notification);
          });

          PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
             console.log('Push action performed: ', notification);
             const data = notification.notification.data;
             if (data && data.poolId) {
                 setPoolId(data.poolId);
                 setView('pool');
             }
          });

        } catch (e) {
          console.log("PushNotifications not available on web.");
        }
      };
      initPush();
      return () => {
        try {
          const p = PushNotifications.removeAllListeners();
          if (p && typeof p.catch === 'function') p.catch(() => {});
        } catch(e){}
      };
    }
  }, [user]);

  if (loading) {
    return (
      <div className="bg-gray-900 min-h-screen flex items-center justify-center text-white">
        <Icon name="spinner" /> {t('loading')}
      </div>
    );
  }



  if (view === 'privacy_policy') {
    return <PrivacyPolicy onBack={() => {
      window.history.pushState({}, '', '/');
      setView('dashboard'); // Or 'auth'/'landing' depending on auth state, but dashboard handles auth check inside
      // actually if user is not logged in, we should probably set 'landing' or just reset view logic
      // But App component structure: if !user, it renders LandingPage or AuthComponent.
      // So if we are in privacy policy view, we should handle it BEFORE the !user check if we want it public.
    }} />;
  }

  if (view === 'data_deletion') {
    return <ExclusaoDados onBack={() => {
      window.history.pushState({}, '', '/');
      setView('dashboard');
    }} />;
  }

  if (view === 'terms_of_service') {
    return <TermsOfService onBack={() => {
      window.history.pushState({}, '', '/');
      setView('dashboard');
    }} />;
  }

  if (view === 'bolao_copa_2026') {
    return <BolaoCopa2026 onBack={() => {
      window.history.pushState({}, '', '/');
      setView('dashboard');
    }} onLoginClick={() => {
      window.history.pushState({}, '', '/');
      setShowLogin(true);
      setView('dashboard');
    }} />;
  }

  if (view === 'regras_bolao_online') {
    return <RegrasBolaoOnline onBack={() => {
      window.history.pushState({}, '', '/');
      setView('dashboard');
    }} onLoginClick={() => {
      window.history.pushState({}, '', '/');
      setShowLogin(true);
      setView('dashboard');
    }} />;
  }

  if (view === 'organizar_bolao_amigos') {
    return <OrganizarBolaoAmigos onBack={() => {
      window.history.pushState({}, '', '/');
      setView('dashboard');
    }} onLoginClick={() => {
      window.history.pushState({}, '', '/');
      setShowLogin(true);
      setView('dashboard');
    }} />;
  }

  if (!user) {
    if (showLogin) {
      return <AuthComponent />;
    }
    return <LandingPage onLoginClick={() => setShowLogin(true)} />;
  }

  const renderView = () => {
    switch (view) {
      case 'pool':
        return <PoolView user={user} poolId={poolId} setView={setView} />;
      case 'admin':
        return <AdminPanel user={user} />;
      case 'admin_dashboard':
        return <AdminDashboard onBack={() => setView('dashboard')} />;
      case 'privacy_policy': // Should not be reached if handled above, but for safety
        return <PrivacyPolicy onBack={() => setView('dashboard')} />;
      case 'dashboard':
      default:
        return <Dashboard user={user} setView={setView} setPoolId={setPoolId} />;
    }
  };

  if (updateRequired) {
    return <ForceUpdateModal />;
  }

  return (
    <div className="bg-gray-900 text-white min-h-screen font-sans">
      <header 
        className="bg-gray-800/80 backdrop-blur-sm px-4 pb-4 shadow-lg flex justify-between items-center sticky top-0 z-10 gap-2"
        style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
      >
        <h1 className="text-lg sm:text-2xl font-bold text-teal-400 leading-tight">{t('headerTitle')}</h1>
        <div className="flex items-center gap-2 sm:gap-4">
          <LanguageSwitcher />
          <span className="text-gray-300 text-sm sm:text-base hidden xs:block">{t('helloUser', { name: user.displayName })}</span>
          {user.uid === ADMIN_UID && (
            <>
              <button onClick={() => setView('admin_dashboard')} className="text-teal-400 hover:text-teal-300 text-sm sm:text-base font-semibold">Dash</button>
              <button onClick={() => setView('admin')} className="text-gray-300 hover:text-white text-sm sm:text-base">Admin</button>
            </>
          )}
          <button onClick={() => signOut(auth)} className="flex items-center text-red-400 hover:text-red-300 font-semibold text-sm sm:text-base">
            <Icon name="logout" /> <span className="hidden sm:inline">{t('logout')}</span>
          </button>
        </div>
      </header>
      <main>
        {renderView()}
      </main>

      {user && showBonusWarning && !Capacitor.isNativePlatform() && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[100] p-4 animate-fade-in">
          <div className="bg-gray-800 border border-yellow-500/50 rounded-xl p-6 max-w-md w-full relative shadow-2xl">
            <h3 className="text-xl font-bold text-yellow-400 mb-4 flex items-center gap-2">
              <span className="text-2xl">⚠️</span> Aviso importante
            </h3>
            <div className="space-y-4 text-gray-300 text-sm">
              <p>
                Foi realizada uma atualização na regra de cálculo dos pontos do Palpite/Bônus (Campeão, Vice e Time do Artilheiro).
              </p>
              <p>
                Para garantir que a pontuação e o ranking estejam corretos, consulte-os pelo site, que já está atualizado.
              </p>
              <p className="font-semibold text-white bg-red-500/20 p-3 rounded-lg border border-red-500/30">
                Caso utilize o aplicativo, verifique se ele está na versão mais recente. Em versões antigas, a pontuação do Palpite/Bônus pode ser exibida incorretamente.
              </p>
              <p className="font-bold text-center text-teal-400">
                O site é a fonte oficial para consulta da pontuação e do ranking.
              </p>
            </div>
            <button
              onClick={() => setShowBonusWarning(false)}
              className="mt-6 w-full bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-bold py-3 rounded-lg transition-colors"
            >
              Entendido
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
